import {
  BuildingCandidate,
  HmlrMatch,
  LeadCandidate,
  LeadSearchViewportBounds,
  ProximityContext,
  SelectedOrg,
  TenantMatch,
  VoaRefetchDegradedOperationResult,
  useAsyncOperation,
  useLeads,
  useLeadSearchOperations,
  useOperation,
  useSiteList,
} from "@inrange/building-manager-api-client";
import { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  Alert,
  Badge,
  Button,
  Container,
  Form,
  Modal,
  OverlayTrigger,
  Table,
  Tooltip,
} from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import Select from "react-select";
import {
  OPERATIONAL_STATUSES,
  STATUS_OPTIONS,
} from "../site/operationalStatusUtils";
import { EnrichmentTierBadge } from "./EnrichmentTierBadge";
import LeadEditPanel, { InputContext, SavePayload } from "./LeadEditPanel";
import LeadsHelpModal from "./LeadsHelpModal";
import {
  leadValueBadgeProps,
  leadValueLabel,
  leadValueSortWeight,
  leadValueTooltip,
  tenantCommercialPending,
} from "./leadValueDisplay";
import { useSettleCommercialPending } from "./useSettleCommercialPending";
import { WithTooltip } from "./WithTooltip";
import LeadsOverviewMap, { MapViewport, OverviewPin } from "./LeadsOverviewMap";
import {
  distinctOrgNameCount,
  isAutoPickable,
  isLandlordAutoPickable,
  OWNER_OCCUPIED_LANDLORD_LABEL,
} from "./leadDisplay";
import { formatEtaSeconds } from "./enrichmentCopy";
import {
  bboxesOverlap,
  haversineMetres,
  polygonBbox,
  polygonsOverlap,
} from "./leadGeo";
import {
  agenticDisplayName,
  noSelectionOptionsText,
  researchedBlankLandlordStatus,
} from "./leadResearchDisplay";
import {
  planBulkResearchContinuation,
  researchRowChipState,
  type BulkResearchResultShape,
} from "./leadResearchProgress";
import {
  loadPersistedResearchOp,
  savePersistedResearchOp,
} from "./researchOpStorage";
import { useIsCompactLayout } from "./useIsCompactLayout";
import { formatAddressCasing } from "./addressCasing";

interface SiteSearchOption {
  label: string;
  value: string;
  name: string;
  // The postcode area the search seeds from. Null when the site has no
  // resolvable UK outcode - the option is still listed (so the picker
  // shows every site, like the Document Numbers picker), but selecting it
  // surfaces an inline "can't search near this site" message.
  outcode: string | null;
  // The site's own coordinates, used to drop a reference marker on the
  // results map so the operator can see where they anchored the search.
  lat: number | null;
  lon: number | null;
  operational: boolean;
}

// The search seeds from a site's postcode outcode. `site.outcode` is only
// populated during site decoration (a UK address with a resolvable
// postcode), so plenty of valid UK sites carry it as null - fall back to
// parsing the outward code out of the address so those sites stay
// searchable rather than silently dropping out of the picker.
// A full UK postcode ("BS15 3AA") anywhere in the address.
const UK_FULL_POSTCODE_RE = /[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}/;
// A bare outward code on its own ("BS15", "SW1A", "M1", "EC1A").
const UK_OUTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?$/;
const deriveSiteOutcode = (site: {
  outcode?: string;
  address?: string;
}): string | null => {
  if (site.outcode && site.outcode.trim()) {
    return site.outcode.trim().toUpperCase();
  }
  const address = (site.address ?? "").toUpperCase();
  // Prefer a full postcode anywhere in the address, taking its outward
  // part (strip the 3-char inward code, tolerating a missing space:
  // "BS15 3AA" / "BS153AA" -> "BS15").
  const full = address.match(UK_FULL_POSTCODE_RE);
  if (full) {
    const postcode = full[0].replace(/\s+/g, "");
    return postcode.slice(0, postcode.length - 3) || null;
  }
  // Otherwise many site addresses lead with the bare outward code as
  // their first comma-separated field ("BS15, Hanham, Bristol, ...").
  // Only treat it as an outcode when that whole first field IS one, so
  // "10 High St, ..." / "M1 Business Park, ..." don't false-match.
  const firstField = address.split(",")[0].trim();
  if (UK_OUTCODE_RE.test(firstField)) {
    return firstField;
  }
  return null;
};

type SortDir = "asc" | "desc" | null;
type SortKey =
  | "address"
  | "locationConfidence"
  | "matchConfidence"
  | "combinedBuildingConfidence"
  | "overallConfidence"
  | "surfaceAreaSqm"
  | "tenantConfidence"
  | "tenantName"
  | "leadValue"
  | "landlordConfidence"
  | "landlordName"
  | "status";
type CandStatus = "new" | "reviewed" | "rejected" | "alreadyLead";

// Combined "is this the right building?" score, surfaced on the
// Building column in place of the two source-specific numbers.
// Min of (1) the geocoder's own self-assessment and (2) BMA's
// polygon-vs-VOA match score, on the same weakest-link principle as
// `leadConfidence` (min of polygon, landlord, tenant). If only one
// signal is present we use that one rather than nulling the row -
// the badge tooltip names which side was missing.
const combinedBuildingConfidence = (
  locationConfidence: number | null,
  buildingConfidence: number | null
): number | null => {
  if (locationConfidence == null && buildingConfidence == null) return null;
  if (locationConfidence == null) return buildingConfidence;
  if (buildingConfidence == null) return locationConfidence;
  return Math.min(locationConfidence, buildingConfidence);
};

// Hover text for the Tenant Conf badge on the left table. Per-
// source explanation so the operator can see whether the score is
// driven by CH SIC matching, OSM proximity, or Places nearby.
const buildTenantTooltip = (
  match: { source?: string | null; confidence: number | null } | null
): string => {
  if (!match) return "No tenant match.";
  const conf =
    match.confidence == null ? "—" : `${Math.round(match.confidence)}%`;
  const sourceLine = (() => {
    switch (match.source) {
      case "ch":
        return "Companies House: postcode-keyed search; ranked by SIC-code match, active status, address fuzziness.";
      case "osm":
        return "OpenStreetMap: tagged-business proximity to the building polygon.";
      case "google_places":
        return "Google Places: nearby-search around the building; ranked by distance + name similarity.";
      default:
        return "Tenant match from upstream pipeline.";
    }
  })();
  return `Tenant: ${conf}\n${sourceLine}`;
};

// Hover text for the Landlord Conf badge. HMLR-only; explains the
// distance + polygon-overlap basis.
const buildLandlordTooltip = (
  match: { confidence: number | null; distanceMetres?: number | null } | null
): string => {
  if (!match) return "No landlord match.";
  const conf =
    match.confidence == null ? "—" : `${Math.round(match.confidence)}%`;
  const distLine =
    match.distanceMetres != null
      ? `Deed centroid ${Math.round(match.distanceMetres)}m from building polygon.`
      : null;
  return [
    `Landlord: ${conf}`,
    "HMLR title deed, polygon-filtered to deeds overlapping the selected building.",
    distLine,
  ]
    .filter(Boolean)
    .join("\n");
};

// Default search runs 5 stages in series (geocode, building, HMLR, tenants,
// EPC) with internal parallelism. EPC is the bottleneck (sequential
// search+fetch per unique postcode at ~3s/postcode), so ~0.3s/row is the
// empirical average across typical BS-area searches; the 8s floor covers
// setup/finalising overhead even on a 5-row search. Shared between the
// auto-confirm gate in runSearch and the modal body so they can't drift.
const estimateCheapSearchSeconds = (pendingEnrichmentRows: number): number =>
  Math.max(8, Math.ceil(pendingEnrichmentRows * 0.3));

// Below this ETA the confirm-enrichment modal is auto-confirmed - a sub-10s
// wait isn't worth the extra click, and the operator can still cancel by
// closing the tab.
const AUTO_CONFIRM_ETA_SECONDS = 10;

// Chunk size for the auto-fire premium-enrich loop. The BE endpoint
// runs synchronously inside API Gateway's 29s ceiling; each premium
// pass is ~2-3s/row (Google Places Text + Places Nearby per row), so
// 5 rows/batch ≈ 15s + cold-start headroom keeps every round-trip
// safely under the limit. Larger batches return 503 on cold starts -
// the symptom that motivated this chunking. Mirrors the inbox bulk-
// premium-enrich's LRO worker conservatism; if a future BE move to
// LRO removes the timeout, we can raise this or drop the chunking
// altogether.
const PREMIUM_AUTO_FIRE_BATCH_SIZE = 5;

// The server-side worker reports premium as stage index 4 (the 5th) in
// a 5-stage run (mirrors _PREMIUM_STAGE_INDEX in operation_lead_search.py).
// current >= this, with total >= 5, means the base passes are done and
// the premium pass is running - the window in which we progressively
// reveal rows + light up "$" per row.
const PREMIUM_STAGE_INDEX = 4;
// Poll the current results roughly once per premium row (~2-3s each with
// the server chunk size of 1) so "$" fills in smoothly without hammering.
const PREMIUM_PEEK_INTERVAL_MS = 3000;

// A cold-start 503 (or a 429/502/504/network blip) from a batched BE endpoint is
// transient - the Lambda just needs a moment. Retry the batch a few times with
// backoff before counting it failed, so a cold search stops surfacing spurious
// "N batches failed" errors. A real 4xx (bad request, not-found) is not retried.
const BATCH_MAX_ATTEMPTS = 3;
const BATCH_RETRY_BASE_MS = 1000;
const isTransientHttpError = (err: unknown): boolean => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === undefined) return true; // network / timeout / no response
  return [429, 502, 503, 504].includes(status);
};

// Stricter sibling for endpoints whose work is NOT safe to run twice: only a
// 429, which is refused before the handler runs.
//
// 503 is deliberately NOT retried here. Prod logs for the 11-row "Retry missing
// data" failure show all three batches ending `Status: timeout, Duration:
// 30000.00 ms` - on this API a 503 is the gateway surfacing a Lambda that ran
// the full 30s and was killed mid-work, NOT one that never started. Retrying it
// would start a second concurrent 30s run re-hitting Overpass / Companies House
// and racing writes on the same VOA rows. Same reasoning excludes 504 and a
// client-side timeout (no status).
const isNotYetExecutedHttpError = (err: unknown): boolean =>
  (err as { response?: { status?: number } })?.response?.status === 429;

// Every batched-BE-call loop on this page drives its batches through here.
// Keeping one implementation is load-bearing: the premium auto-fire grew the
// retry above while the degraded-source retry did not, so an operator-triggered
// "Retry missing data" lost all 11 of its rows to a single cold-start 503 that
// the sibling path would have shrugged off.
const runBatchWithRetry = async <T,>(
  attemptFn: () => Promise<T>,
  shouldRetry: (err: unknown) => boolean = isTransientHttpError
): Promise<{ ok: true; value: T } | { ok: false; error: unknown }> => {
  let lastErr: unknown = null;
  for (let attempt = 1; attempt <= BATCH_MAX_ATTEMPTS; attempt++) {
    try {
      return { ok: true, value: await attemptFn() };
    } catch (err) {
      lastErr = err;
      if (attempt < BATCH_MAX_ATTEMPTS && shouldRetry(err)) {
        // Linear backoff + jitter; a cold Lambda is usually warm by the
        // second attempt.
        await new Promise((r) =>
          setTimeout(r, BATCH_RETRY_BASE_MS * attempt + Math.random() * 300)
        );
        continue;
      }
      break;
    }
  }
  return { ok: false, error: lastErr };
};

// Per-row paid-enrichment cost. ~$0.01 (Google Places Text + Places
// Nearby at Essentials field mask). Defined once so the per-row link
// in the right panel and the bulk modal stay aligned.
const PREMIUM_ENRICH_COST_PER_ROW = 0.01;

// Below this many returned rows we auto-prompt the operator to
// premium-enrich the whole result set. Small searches are cheap
// enough (< $1) that paying up front for the upgraded geocode +
// Google-Places tenant matches usually beats per-row review.
const PREMIUM_ENRICH_PROMPT_THRESHOLD = 100;

const formatEnrichCost = (rows: number): string =>
  `$${(rows * PREMIUM_ENRICH_COST_PER_ROW).toFixed(2)}`;

// The ceiling the single-row "Research landlord (up to ~$2)" button advertises;
// bulk research reuses it for the total-cost confirm (real spend is ~$0.20/row,
// this is the conservative ceiling shown to the operator).
const RESEARCH_COST_CEILING_PER_ROW = 2;

interface PersistedResearchOp {
  operationKey: string;
  siteId: string;
  sourceRef: string;
}
const RESEARCH_OP_STORAGE_KEY = "inrange.leads.searchResearchOp";
// The degraded-source retry LRO's handle. Persisted for the same reason as the research
// handle: the worker keeps going regardless of the browser, so a reload / navigating away
// would otherwise lose the poll and the refreshed rows would never merge into the table
// (they land in Postgres, but the operator sees the stale degraded badge until they
// re-search) - and the button would go clickable again mid-run.
interface PersistedRefetchDegradedOp {
  operationKey: string;
  siteId: string;
  total: number;
  // Rows beyond the BE's per-operation cap, reported so the completion message can tell
  // the operator to click again for the rest.
  overflow: number;
}
const REFETCH_DEGRADED_OP_STORAGE_KEY = "inrange.leads.searchRefetchDegradedOp";

// Roughly 1s per row across 24-way parallel workers, with a floor of
// 3s to cover spin-up + result-write overhead even for tiny batches.
const estimatePremiumEnrichSeconds = (rows: number): number =>
  Math.max(3, rows * 1);

const SortHeader: React.FC<{
  label: string;
  sortKey: SortKey;
  sort: { key: SortKey; dir: SortDir } | null;
  setSort: (s: { key: SortKey; dir: SortDir } | null) => void;
}> = ({ label, sortKey, sort, setSort }) => {
  const active = sort?.key === sortKey ? sort.dir : null;
  const onClick = () => {
    const next: SortDir =
      active === null ? "desc" : active === "desc" ? "asc" : null;
    setSort(next === null ? null : { key: sortKey, dir: next });
  };
  const arrow = active === "desc" ? " ▼" : active === "asc" ? " ▲" : "";
  return (
    <th style={{ cursor: "pointer", userSelect: "none" }} onClick={onClick}>
      {label}
      {arrow}
    </th>
  );
};

const ConfidenceBadge: React.FC<{
  confidence: number | null;
  // Optional hover-tooltip explaining how the number was derived.
  // Multi-line plain text - rendered via Bootstrap OverlayTrigger so
  // the operator can read why a row scored what it did without
  // having to learn the scoring formula by osmosis.
  tooltip?: string;
}> = ({ confidence, tooltip }) => {
  if (confidence == null) {
    const dash = (
      <span
        style={{
          color: "#aaa",
          fontSize: 11,
          cursor: tooltip ? "help" : undefined,
        }}
      >
        -
      </span>
    );
    if (!tooltip) return dash;
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip style={{ whiteSpace: "pre-line", textAlign: "left" }}>
            {tooltip}
          </Tooltip>
        }
      >
        {dash}
      </OverlayTrigger>
    );
  }
  const colour =
    confidence >= 70 ? "#28a745" : confidence >= 40 ? "#ffc107" : "#6c757d";
  const badge = (
    <span
      style={{
        marginLeft: 4,
        padding: "0 4px",
        background: colour,
        color: "#fff",
        fontSize: 10,
        borderRadius: 3,
        cursor: tooltip ? "help" : undefined,
      }}
    >
      {Math.round(confidence)}%
    </span>
  );
  if (!tooltip) return badge;
  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip style={{ whiteSpace: "pre-line", textAlign: "left" }}>
          {tooltip}
        </Tooltip>
      }
    >
      {badge}
    </OverlayTrigger>
  );
};

const StatusBadge: React.FC<{ status: CandStatus }> = ({ status }) => {
  const meta: Record<CandStatus, { label: string; bg: string; text?: "dark" }> =
    {
      new: { label: "New", bg: "secondary" },
      reviewed: { label: "Reviewed", bg: "info" },
      rejected: { label: "Rejected", bg: "dark" },
      alreadyLead: { label: "Lead", bg: "warning", text: "dark" },
    };
  const m = meta[status];
  // Match the sibling markers in the same Status cell ("On platform", tags),
  // which are all fontSize 10 - the default Bootstrap badge size left this
  // pill visibly larger than its neighbours.
  return (
    <Badge bg={m.bg} text={m.text} style={{ fontSize: 10 }}>
      {m.label}
    </Badge>
  );
};

const candidateStatus = (c: LeadCandidate): CandStatus => {
  if (c.existingLeadId) return "alreadyLead";
  if (c.rejectedAt) return "rejected";
  if (c.reviewedAt) return "reviewed";
  return "new";
};

// Human labels for the persisted `enrichmentDegradedSources` enum.
// Keeping the mapping FE-side (rather than asking BE to ship labels)
// matches how `SelectedOrg.source` is treated. New values added on the
// BE without an entry here just render with the raw key.
const DEGRADED_SOURCE_LABELS: Record<string, string> = {
  overpass: "OpenStreetMap",
  companies_house: "Companies House",
  google_places: "Google Maps",
  company_identity: "company lookup",
};

const formatDegradedSources = (sources: string[]): string =>
  sources.map((s) => DEGRADED_SOURCE_LABELS[s] ?? s).join(", ");

// Small "Missing data" pill rendered alongside the row Status badge.
// Tooltip names the affected sources so the operator knows what's
// missing without opening the right panel. The bulk refetch banner at
// the top of the table is the recovery path - per-row refetch lives
// inside LeadEditPanel.
const DegradedBadge: React.FC<{ sources: string[] }> = ({ sources }) => {
  if (sources.length === 0) return null;
  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={`degraded-${sources.join("-")}`}>
          Upstream rate-limited or unavailable when this row was enriched -
          missing: {formatDegradedSources(sources)}. Use the &ldquo;Retry
          missing data&rdquo; banner above the table to retry, or open the row
          and refetch from the panel.
        </Tooltip>
      }
    >
      <Badge
        bg="warning"
        text="dark"
        style={{ marginLeft: 4, fontSize: 10, cursor: "help" }}
      >
        Missing{" "}
        {sources.length === 1 ? "1 source" : `${sources.length} sources`}
      </Badge>
    </OverlayTrigger>
  );
};

// Compact "(D)" mark shown next to the address of a row whose resolved building
// is already claimed by a row ABOVE it in the list. Non-blocking: VOA lists
// several rateable units per building and a large shed can have more than one
// real occupier, so this flags the overlap for the operator rather than hiding
// the row. Rendered next to the address on the results table and in the right
// pane header so both surfaces mark the same rows.
export const DuplicateBuildingMark: React.FC<{
  present: boolean;
  firstAddress?: string | null;
}> = ({ present, firstAddress }) => {
  if (!present) return null;
  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={`dup-building-${firstAddress ?? "row"}`}>
          Duplicate building: resolves to the same building as
          {firstAddress ? ` "${firstAddress}"` : " another row"} higher in this
          list, so both would become leads for one physical site. The tenant
          agreeing is expected - tenant lookup is anchored on the building - so
          it is not extra evidence.
        </Tooltip>
      }
    >
      <span
        style={{
          marginRight: 5,
          fontSize: 10,
          fontWeight: 700,
          color: "#0d6efd",
          cursor: "help",
        }}
      >
        (D)
      </span>
    </OverlayTrigger>
  );
};

// Cost-warning copy + ETA helper live in the shared enrichmentCopy
// module so the per-row LeadEditPanel confirms render the same Basic
// vs Premium framing. The renderer below stays inline (rather than
// being exported alongside the constants) so eslint's
// react-refresh/only-export-components rule on the shared module
// holds - constants in one file, components colocated with their
// only consumer here.

const buildVoaInputContext = (c: LeadCandidate): InputContext => ({
  // VOA candidates carry the assessment ref as sourceRef. The
  // 10-digit number is the VOA Assessment Reference Number (a
  // globally unique identifier the Valuation Office Agency assigns
  // to each rating-list assessment - searchable on
  // https://www.tax.service.gov.uk/business-rates-find/).
  sourceLabel: `VOA · ${c.sourceRef}`,
  address: c.address,
  declaredFloorAreaSqm: c.floorAreaSqm,
  // VOA never declares a building count, so this slot is always null
  // for VOA candidates. CSV-imported leads populate it from their
  // `number_of_buildings` column on the import row.
  declaredNumBuildings: null,
  geocodeSummary:
    [c.geocodeSource, c.geocodeConfidence].filter(Boolean).join(" ") || null,
  geocodeAttempts: c.geocodeAttempts,
  // Whole LeadCandidate JSON for the "View raw source data" modal -
  // the operator can pop this open and see every field we got from
  // the VOA Rating List + every downstream enrichment.
  rawSource: c as unknown as Record<string, unknown>,
  epcMatches: c.epcMatches ?? [],
});

// Filter HMLR matches the same way the right panel does
// (`filteredHmlrMatches` in LeadEditPanel): bbox pre-reject +
// polygon-overlap geometric test against the auto-selected building.
// Used by the left-panel Landlord column + saved-selection
// validation so the table never claims a deed the right panel
// hides.
const filterHmlrToCurrentBuilding = (c: LeadCandidate): HmlrMatch[] => {
  if (c.hmlrMatches.length === 0) return [];
  const defaultBuilding = c.buildingCandidates?.[0];
  const buildingBbox = defaultBuilding
    ? polygonBbox(defaultBuilding.polygon)
    : null;
  if (!buildingBbox || !defaultBuilding) return c.hmlrMatches;
  return c.hmlrMatches.filter((m) => {
    if (!m.polygon) return true;
    const mb = m.bbox ?? polygonBbox(m.polygon);
    if (mb && !bboxesOverlap(buildingBbox, mb)) return false;
    return polygonsOverlap(defaultBuilding.polygon, m.polygon, {
      lat: defaultBuilding.centroidLat,
      lon: defaultBuilding.centroidLon,
    });
  });
};

const effectiveTopLandlord = (c: LeadCandidate): HmlrMatch | null =>
  filterHmlrToCurrentBuilding(c)[0] ?? null;

// Headline "overall" confidence rendered in the leftmost column on
// the results table and sorted by default. Min of the three signals
// the right-panel header also shows: combined building (location +
// polygon match), top filtered HMLR landlord, top tenant. Null when
// none of the three are computable - row sorts to the bottom under
// desc. Additionally, if the row has no building polygon at all,
// the overall is hard-capped well below the premium-enrich gate -
// even a 100% landlord + 100% tenant row is a dead end without a
// building to attach to, so it shouldn't read as a confident row.
const NO_BUILDING_OVERALL_CAP = 60;
const overallConfidenceForCandidate = (c: LeadCandidate): number | null => {
  const combinedBldg = combinedBuildingConfidence(
    locationConfidenceForCandidate(c),
    c.matchConfidence ?? null
  );
  const landlord = effectiveTopLandlord(c)?.confidence ?? null;
  const tenant = c.tenantMatches[0]?.confidence ?? null;
  const values = [combinedBldg, landlord, tenant].filter(
    (v): v is number => v != null
  );
  if (values.length === 0) return null;
  const score = Math.min(...values);
  if ((c.buildingCandidates?.length ?? 0) === 0) {
    return Math.min(score, NO_BUILDING_OVERALL_CAP);
  }
  return score;
};

// True when `c.selectedLandlord` still corresponds to one of the
// HMLR matches that survives the current building's polygon-overlap
// filter. Prevents the left-table Landlord column from advertising
// a saved pick the right panel has flagged as no longer in scope.
const savedLandlordIsValid = (c: LeadCandidate): boolean => {
  if (!c.selectedLandlord) return false;
  // Platform / manual selections aren't bound to the source-side
  // match lists (they're operator-typed or platform-org picks), so
  // they're always valid - they don't need to appear in the HMLR
  // matches. Without this, picking a platform org as Landlord and
  // hitting Save makes the selection vanish on next render because
  // the left-table cell and the promote-selection builder both
  // re-derive from the top HMLR match.
  if (
    c.selectedLandlord.source === "platform" ||
    c.selectedLandlord.source === "manual"
  ) {
    return true;
  }
  // Agentic candidates live in `agenticMatches`, so validate against that
  // list (mirroring the tenant validator) rather than the HMLR matches.
  if (c.selectedLandlord.source === "agentic") {
    return c.agenticMatches.some(
      (m) => agenticDisplayName(m) === c.selectedLandlord!.displayName
    );
  }
  const filtered = filterHmlrToCurrentBuilding(c);
  return filtered.some(
    (m) =>
      m.proprietorName === c.selectedLandlord!.displayName ||
      (m.titleNumber != null &&
        m.titleNumber === c.selectedLandlord!.hmlrTitleNumber)
  );
};

// True when `c.selectedTenant` still corresponds to one of the
// current tenant matches. Tenants aren't building-filtered (the
// right panel just shows all of them), so this only catches the
// case where a refetch has dropped the previously-saved tenant
// from the match list.
const savedTenantIsValid = (c: LeadCandidate): boolean => {
  if (!c.selectedTenant) return false;
  // Same platform/manual carve-out as the landlord validator.
  if (
    c.selectedTenant.source === "platform" ||
    c.selectedTenant.source === "manual"
  ) {
    return true;
  }
  return c.tenantMatches.some(
    (m) =>
      m.companyName === c.selectedTenant!.displayName ||
      (m.googlePlaceId != null &&
        m.googlePlaceId === c.selectedTenant!.googlePlaceId)
  );
};

// Pick out the candidate that backs a saved selection and return
// its confidence. SelectedOrg doesn't carry confidence on the wire
// (it's a chosen-org descriptor, not a match record), so we look
// up the original match by source-keyed identifier (CH company
// number, Places ID, OSM ID, or proprietor name) to surface the
// real number. Without this the left panel would either omit the
// badge for saved selections or hard-code an invented value, and
// the right panel's match-table would disagree with the left's
// column on the same row.
const tenantConfidenceForSelection = (
  matches: TenantMatch[],
  sel: SelectedOrg
): number | null => {
  for (const m of matches) {
    if (sel.chCompanyNumber && m.companyNumber === sel.chCompanyNumber) {
      return m.confidence;
    }
    if (sel.googlePlaceId && m.googlePlaceId === sel.googlePlaceId) {
      return m.confidence;
    }
    if (sel.osmId && m.osmId === sel.osmId) {
      return m.confidence;
    }
    if (m.companyName === sel.displayName) {
      return m.confidence;
    }
  }
  return null;
};
const landlordConfidenceForSelection = (
  matches: HmlrMatch[],
  sel: SelectedOrg
): number | null => {
  for (const m of matches) {
    if (sel.hmlrTitleNumber && m.titleNumber === sel.hmlrTitleNumber) {
      return m.confidence;
    }
    if (m.proprietorName === sel.displayName) {
      return m.confidence;
    }
  }
  return null;
};
// Agentic landlord candidates live in `agenticMatches`, never in
// `hmlrMatches` (the eligibility contract keeps them out of the auto-pick
// paths). So an explicitly-selected agentic landlord's confidence has to be
// resolved from that list, or the left-table Lnd column shows blank while the
// right panel shows e.g. 42% - the two surfaces must agree.
const agenticConfidenceForSelection = (
  matches: LeadCandidate["agenticMatches"],
  sel: SelectedOrg
): number | null => {
  for (const m of matches) {
    if (agenticDisplayName(m) === sel.displayName) {
      return m.confidence;
    }
  }
  return null;
};

// Friendly labels for the BE's promote-skip reason codes. Anything
// not in this map (e.g. ad-hoc "error: ..." messages from the BE)
// gets passed through verbatim so the operator still sees the raw
// reason rather than a generic "skipped".
const PROMOTE_SKIP_LABELS: Record<string, string> = {
  already_a_lead: "already a lead",
  not_found: "VOA row not found",
  not_reviewed: "row not yet reviewed",
};

// Plain-English summary of the active search filters - shown in the
// "matches X rows" truncation banner and the pre-search confirm modal
// so the operator can read back what they just searched for at a
// glance ("4,495 BS11 warehouses ≥10,000 m² within 10 km" beats just
// "4,495 rows").
const summariseSearchTerms = (params: {
  postcodeOutward: string;
  minFloorAreaSqm: string;
  radiusKm: string;
  nearSiteName?: string;
}): string => {
  const parts: string[] = ["warehouses"];
  if (params.minFloorAreaSqm) {
    parts.push(`≥ ${Number(params.minFloorAreaSqm).toLocaleString()} m²`);
  }
  if (params.postcodeOutward.trim()) {
    // The seed is the selected site's postcode area; prefer the site
    // name in the summary but fall back to the raw outcode for any
    // deep-link that carries only a postcodeOutward.
    const seed = params.nearSiteName
      ? `near ${params.nearSiteName}`
      : `in ${params.postcodeOutward.trim().toUpperCase()}`;
    parts.push(seed);
    if (params.radiusKm && Number(params.radiusKm) > 0) {
      parts.push(`+ ${params.radiusKm} km of neighbouring outcodes`);
    } else {
      parts.push("only");
    }
  } else {
    parts.push("nationally");
  }
  return parts.join(" ");
};

// Map a geocoder confidence label onto a 0-100 percent so the left-
// table Location column can render in the same shape as Building /
// Tenant / Landlord confidence cells.
const LOCATION_CONFIDENCE_BY_LABEL: Record<string, number> = {
  HIGH: 95,
  MEDIUM: 70,
  LOW: 35,
  POSTCODE_CENTROID: 15,
};
// A geocoder match alone isn't useful if we couldn't find any
// building polygon to act on. The operator can't pick a building,
// the area-match factor can't be computed, the row is a dead end.
// We treat "no building candidates" as "location confidence
// unknown" rather than carrying the geocoder's self-assessment
// forward into a number the operator might trust. The combined
// building confidence falls out naturally: min(null, null) = null,
// so the row shows "-" on the Building column too.
const locationConfidenceForCandidate = (c: LeadCandidate): number | null => {
  const hasBuildings =
    (c.buildingCandidates?.length ?? 0) > 0 || c.matchConfidence != null;
  if (!hasBuildings) return null;
  const label = c.geocodeConfidence?.toUpperCase();
  if (!label) return null;
  return LOCATION_CONFIDENCE_BY_LABEL[label] ?? null;
};

const LeadSearchPage: React.FC = () => {
  document.title = "Search for leads";
  const {
    searchLeadCandidates,
    peekLeadCandidates,
    reviewVoaCandidate,
    rejectVoaCandidate,
    refetchVoaCandidate,
    refetchVoaDegradedSources,
    premiumEnrichVoaCandidates,
    researchVoaLandlord,
    bulkResearchVoaLandlords,
    cancelResearchRow,
    promoteVoaCandidates,
    fetchLeads,
  } = useLeads();

  // Search criteria, sort, and the right-panel selection are persisted
  // into the URL so the operator can deep-link a specific review state.
  // Selecting a row PUSHES history (so the browser back button returns
  // to the overall results); everything else REPLACES (typing in the
  // search form shouldn't burn a history entry per keystroke).
  const [searchParams, setSearchParams] = useSearchParams();
  const postcodeOutward = searchParams.get("postcodeOutward") ?? "";
  const minFloorAreaSqm = searchParams.get("minFloorAreaSqm") ?? "10000";
  const radiusKm = searchParams.get("radiusKm") ?? "10";
  // The lead search anchors on an existing InRange site's postcode area
  // ("Near this site"). We persist the picked site id so the typeahead
  // re-selects it on reload / deep-link, and derive `postcodeOutward`
  // from the site's outcode so all downstream search plumbing (request,
  // outcode map boundary, radius expansion) is unchanged.
  const nearSiteId = searchParams.get("nearSiteId") ?? "";
  const selectedSourceRef = searchParams.get("selected");
  const sortKey =
    (searchParams.get("sortKey") as SortKey | null) ?? "overallConfidence";
  const sortDir =
    (searchParams.get("sortDir") as Exclude<SortDir, null> | null) ?? "desc";
  // Memoised so the deps array of the sort `useMemo` below doesn't see a
  // new object reference every render and re-sort the candidate list
  // each time. The actual values only change when the URL changes.
  const sort = useMemo<{ key: SortKey; dir: SortDir } | null>(
    () => (sortKey ? { key: sortKey, dir: sortDir } : null),
    [sortKey, sortDir]
  );

  // "Near this site" typeahead: pick an existing InRange site to anchor
  // the search on its postcode area. Mirrors the DocumentNumbersCard
  // grouped Operational-first picker. The lead search is UK-only (VOA /
  // postcodes.io), so a site with no resolvable UK outcode can never be a
  // valid anchor - the Dutch portfolio filled the list with rows that only
  // said "no postcode". Those are excluded rather than listed-and-rejected.
  const { fetchSites } = useSiteList();
  const siteSelectOptions = useMemo(() => {
    const siteSortKey = (name: string) =>
      name.trim().replaceAll(" ", "").toLowerCase();
    const sites: SiteSearchOption[] = (fetchSites.data?.sites || [])
      .map((site): SiteSearchOption => {
        const outcode = deriveSiteOutcode(site);
        const statusLabel =
          (STATUS_OPTIONS as Record<string, string>)[site.operationalStatus] ||
          site.operationalStatus;
        return {
          label: outcode
            ? `${site.name} ${outcode} (${statusLabel})`
            : `${site.name} (${statusLabel}) - no postcode`,
          value: site.id,
          name: site.name,
          outcode,
          lat: site.latitude ?? null,
          lon: site.longitude ?? null,
          operational: OPERATIONAL_STATUSES.has(site.operationalStatus),
        };
      })
      .filter((s) => s.outcode !== null)
      .sort((a, b) => siteSortKey(a.name).localeCompare(siteSortKey(b.name)));
    const operational = sites.filter((s) => s.operational);
    const other = sites.filter((s) => !s.operational);
    return [
      { label: "Operational", options: operational },
      { label: "Other", options: other },
    ];
  }, [fetchSites.data?.sites]);
  const selectedSite = useMemo(() => {
    if (!nearSiteId) return null;
    for (const group of siteSelectOptions) {
      const found = group.options.find((o) => o.value === nearSiteId);
      if (found) return found;
    }
    return null;
  }, [siteSelectOptions, nearSiteId]);

  const updateParams = (
    patch: Record<string, string | null>,
    opts: { push?: boolean } = {}
  ) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      // Only `null` deletes the key; empty strings are written through
      // so the user can clear a field that has a default (e.g. radius)
      // without the default re-appearing on the next render.
      if (v === null) next.delete(k);
      else next.set(k, v);
    }
    setSearchParams(next, { replace: !opts.push });
  };
  // Picking a site sets both the persisted site id and the derived
  // postcode-area seed; clearing removes both.
  const setNearSite = (site: SiteSearchOption | null) =>
    updateParams({
      nearSiteId: site?.value ?? null,
      postcodeOutward: site?.outcode ?? null,
    });
  const setMinFloorAreaSqm = (v: string) =>
    updateParams({ minFloorAreaSqm: v });
  const setRadiusKm = (v: string) => updateParams({ radiusKm: v });
  // Local, synchronous mirror of the two numeric filter inputs. The
  // canonical value lives in the URL (searchParams), but driving the
  // <input> straight off that jumped the caret to the end mid-edit:
  // setSearchParams commits a render behind the keystroke, so React briefly
  // re-applied the stale URL value to the DOM node and the caret moved.
  // Mirroring into local state keeps the input value in lockstep with the
  // keystroke (caret preserved); the URL is still updated in the same
  // handler so every downstream consumer of minFloorAreaSqm / radiusKm is
  // unchanged.
  const [minFloorAreaInput, setMinFloorAreaInput] = useState(minFloorAreaSqm);
  const [radiusKmInput, setRadiusKmInput] = useState(radiusKm);
  // Resync when the URL value changes from outside the input itself (reset,
  // deep-link, browser back/forward). No-op during typing since local
  // already equals the value we just wrote to the URL.
  useEffect(() => {
    setMinFloorAreaInput(minFloorAreaSqm);
  }, [minFloorAreaSqm]);
  useEffect(() => {
    setRadiusKmInput(radiusKm);
  }, [radiusKm]);
  const setSelectedSourceRef = (v: string | null) =>
    // Push history so the browser back button returns to the overall
    // search results. The panel's `useBlocker` intercepts this when
    // there are unsaved edits and shows the discard prompt - no
    // parent-side confirm needed (and a parent-side one would
    // double-prompt).
    updateParams({ selected: v }, { push: true });
  const setSort = (next: { key: SortKey; dir: SortDir } | null) => {
    updateParams({
      sortKey: next?.key ?? null,
      sortDir: next?.dir ?? null,
    });
  };

  const [candidates, setCandidates] = useState<LeadCandidate[]>([]);

  // Rows whose tenant has not been looked up yet render as "Calculating".
  // Nothing else settles them, so kick the lookups off once and merge the
  // refreshed rows back in place - re-running the search would throw away the
  // operator's scroll position, selection and open panel.
  const commercialPendingRefs = useMemo(
    () =>
      candidates
        .filter((c) => tenantCommercialPending(c.tenantCommercial))
        .map((c) => c.sourceRef),
    [candidates]
  );
  const { recount: recountCommercial, resolving: recountingCommercial } =
    useSettleCommercialPending({
      sourceRefs: commercialPendingRefs,
      onSettled: (commercial) => {
        // Merge the two changed fields in place rather than replacing the row:
        // the operation only sends those two back, and the rest of the row (its
        // polygons, deeds, matches, and any edit in progress) is ours.
        setCandidates((prev) =>
          prev.map((c) => {
            const resolved = commercial[c.sourceRef];
            return resolved
              ? {
                  ...c,
                  tenantCommercial: resolved.tenant,
                  leadValue: resolved.value,
                }
              : c;
          })
        );
      },
    });
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedRowRefs, setSelectedRowRefs] = useState<Set<string>>(
    new Set()
  );

  // Operator-facing filter on the results table by candidate status.
  // null = All (default). Single-select: clicking a status replaces
  // the filter rather than adding to it; clicking the active one
  // (or the "All" chip) clears the filter back to All. Applied
  // before sort, so the result count + sort headers reflect the
  // visible rows only.
  const [statusFilter, setStatusFilter] = useState<CandStatus | null>(null);
  const selectStatusFilter = (s: CandStatus) =>
    setStatusFilter((prev) => (prev === s ? null : s));
  // When the backend returns `status: "pending"` we hold the
  // operationKey + matched-row totals here and poll the async-status
  // endpoint until enrichment finishes; then we re-issue the search
  // and grab the now-cached completed response.
  // Step-5 progress for the auto-fire premium enrich loop. The BE LRO
  // worker only reports 4 stages (geocode / building / HMLR / tenant);
  // premium enrichment is a FE-driven sequential-chunked phase that
  // runs after basic completes. We render this as a continuation of
  // the same progress block so the operator sees one end-to-end
  // pipeline ("Step 5 of 5: Premium enrichment") instead of the
  // progress block disappearing and reappearing.
  const [premiumAutoFireProgress, setPremiumAutoFireProgress] = useState<{
    done: number;
    total: number;
    eligibleRefs: number;
  } | null>(null);
  const [pendingOperation, setPendingOperation] = useState<{
    operationKey: string;
    siteId: string;
    totalMatched: number;
    totalReturned: number;
    truncated: boolean;
    totalPendingEnrichment: number;
    forceRefetch: boolean;
    // `kind="chunked"` means the BE kicked off a Step Functions
    // execution. The FE shows a different banner ("search runs in
    // the background, view progress on the status page") and
    // doesn't try to wait for completion in-line - chunked searches
    // can take 30+ minutes.
    kind?: "sync" | "chunked";
  } | null>(null);
  // Agentic landlord research: the in-flight LRO (polled below) + the AI
  // trace run id captured per row when a run completes, so the panel's
  // "View AI trace" toggle can fetch it for the rest of the session.
  // Lazy-init from sessionStorage so a page refresh mid-research re-attaches the
  // poll (see loadPersistedResearchOp). Persisted on every change by the effect
  // just below.
  const [researchOp, setResearchOp] = useState<PersistedResearchOp | null>(() =>
    loadPersistedResearchOp<PersistedResearchOp>(
      RESEARCH_OP_STORAGE_KEY,
      "sourceRef"
    )
  );
  useEffect(() => {
    savePersistedResearchOp(RESEARCH_OP_STORAGE_KEY, researchOp);
  }, [researchOp]);
  // Bulk landlord research over the multi-selected rows: the in-flight LRO
  // (polled below). One op for the whole batch; on completion we re-run the
  // search so the researched candidates show on each row. `refs` carries the
  // batch's rows so the right panel can show "researching" (and disable the
  // single-row button) for whichever of them the operator opens mid-batch.
  const [bulkResearchOp, setBulkResearchOp] = useState<{
    operationKey: string;
    siteId: string;
    // The refs the BE actually queued for THIS sub-batch (drives the per-row
    // done/researching/queued chip via the worker cursor).
    refs: string[];
    // Every ref this sub-batch was asked to research (= the remaining chain at
    // launch). `inputRefs \ refs` is the overflow that auto-continue will pick up
    // in a later batch (shown "queued"); the no-progress guard compares the
    // completed batch's notProcessed against inputRefs.length.
    inputRefs: string[];
    // Rows the operator has pulled from the queue via the panel's per-row Cancel.
    // Optimistic: added on click, so the row stops showing "queued" immediately; the
    // BE append + worker skip make it durable. Resets with the op when the batch ends.
    cancelledRefs: Set<string>;
  } | null>(null);
  // Rows a finished auto-continue chain never reached (its no-progress guard
  // tripped, or every remaining row failed), so they carry a persistent
  // "not run - run again" chip in the left list. Auto-continue supersedes this in
  // the common case; it mostly shows only when the chain stops early.
  const [researchNotRunRefs, setResearchNotRunRefs] = useState<Set<string>>(
    new Set()
  );
  // When the backend returns `status: "preview"` we hold the row
  // counts + the originating forceRefetch flag here and pop a confirm
  // modal. Confirm => re-issue the same search with
  // `confirmEnrichment: true`. Cancel => clear and do nothing.
  const [pendingPreview, setPendingPreview] = useState<{
    totalMatched: number;
    totalReturned: number;
    truncated: boolean;
    totalPendingEnrichment: number;
    forceRefetch: boolean;
  } | null>(null);
  const [resultMeta, setResultMeta] = useState<{
    totalMatched: number;
    totalReturned: number;
    truncated: boolean;
    failedRowCount?: number;
    failedRowRefs?: string[];
  } | null>(null);

  // Mid-edit (unsaved) right-panel selections, keyed by sourceRef.
  // The left-panel Tenant / Landlord columns prefer this over the
  // saved selection when present, so right-panel edits show up
  // immediately - e.g. clearing the Landlord in the right panel
  // blanks the column in the left panel before the operator hits
  // Save. Cleared on row-switch / refetch (the LeadEditPanel
  // remounts via the `key` prop on those events, which fires its
  // initial useEffect with the freshly-derived defaults).
  const [pendingSelectionByRef, setPendingSelectionByRef] = useState<
    Record<
      string,
      {
        buildingBkey: string | null;
        landlord: SelectedOrg | null;
        tenant: SelectedOrg | null;
        ownerOccupier: boolean;
        hasPv: boolean;
        dirty: boolean;
      }
    >
  >({});
  // What the *current* search is targeting: a typed postcode (+radius) or a
  // map viewport ("Search this area"). Held in state - not re-derived from
  // the form - so the preview-confirm and async-poll re-issues of a single
  // search reuse the same target even if the operator later pans the map or
  // edits the postcode box. Every *new* search passes its target explicitly;
  // re-issues omit it and reuse this.
  // The active target lives in `activeSearchTargetRef` (read synchronously by
  // re-issues); this setter only exists to trigger a re-render when the target
  // flips. The state value itself is no longer read, so it's left unbound.
  const [, setSearchTarget] = useState<
    | { kind: "postcode" }
    | { kind: "viewport"; bounds: LeadSearchViewportBounds }
  >({ kind: "postcode" });
  // Mirror of the active target in a ref. Re-issues of a single search
  // (preview auto-confirm, modal-confirm, async poll, force-refresh) read
  // the target from here, NOT from `searchTarget` state: the synchronous
  // preview -> auto-confirm recursion runs inside the same closure that
  // just called setSearchTarget, so the state hasn't committed yet and a
  // state read would be stale. The ref updates synchronously, so every
  // re-issue - synchronous or in a later render - sees the correct target.
  const activeSearchTargetRef = useRef<
    | { kind: "postcode" }
    | { kind: "viewport"; bounds: LeadSearchViewportBounds }
  >({ kind: "postcode" });
  // Bumped only when a typed-postcode search completes, to drive the overview
  // map's one-shot fit-to-results. A viewport search deliberately never bumps
  // it, so the map stays exactly where the operator panned.
  const [mapFitToken, setMapFitToken] = useState(0);
  // Bumped when ANY search completes (postcode or viewport). The overview
  // map hides the "Search this area" button on this bump - the results now
  // reflect the current view - and re-shows it on the next user pan/zoom.
  const [mapSearchToken, setMapSearchToken] = useState(0);
  // Remembers the overview map's viewport (centre + zoom) as the operator
  // pans/zooms, so clicking into a result and back returns to that exact
  // view instead of re-fitting to all pins (which looked "zoomed out" after
  // a panned viewport search). A ref - it must not trigger re-renders.
  const overviewViewportRef = useRef<MapViewport | null>(null);

  const [promoteModalOpen, setPromoteModalOpen] = useState(false);
  const [promoteTagsRaw, setPromoteTagsRaw] = useState("");
  // Non-null when the operator clicked Promote & next on a single
  // row; the modal opens with that one row's ref captured. The
  // confirm handler promotes only that ref + advances to the next
  // New row instead of acting on the bulk selection.
  const [singlePromoteRef, setSinglePromoteRef] = useState<string | null>(null);

  // Top tag chips shown above the tags input - lets the operator
  // re-use existing tags with one click instead of re-typing. First
  // chip is the most-recently-used tag (even if not the most popular)
  // because operators tend to be working through a single batch
  // session and the tag they just applied is the most likely to be
  // wanted again. Remaining chips are by usage count.
  const topTagChips = useMemo<string[]>(() => {
    const leads = fetchLeads.data?.leads ?? [];
    if (leads.length === 0) return [];
    const counts = new Map<string, number>();
    const lastUsed = new Map<string, number>();
    for (const lead of leads) {
      const tags = lead.tags ?? [];
      const ts = lead.createdAt ? new Date(lead.createdAt).getTime() : 0;
      for (const t of tags) {
        counts.set(t, (counts.get(t) ?? 0) + 1);
        const prev = lastUsed.get(t) ?? 0;
        if (ts > prev) lastUsed.set(t, ts);
      }
    }
    if (counts.size === 0) return [];
    const allTags = [...counts.keys()];
    let mostRecent: string | null = null;
    let mostRecentTs = -Infinity;
    for (const t of allTags) {
      const ts = lastUsed.get(t) ?? 0;
      if (ts > mostRecentTs) {
        mostRecentTs = ts;
        mostRecent = t;
      }
    }
    const byCountDesc = allTags
      .filter((t) => t !== mostRecent)
      .sort((a, b) => (counts.get(b) ?? 0) - (counts.get(a) ?? 0));
    return [mostRecent, ...byCountDesc]
      .filter((t): t is string => t != null)
      .slice(0, 5);
  }, [fetchLeads.data]);

  const addTagChipToInput = (tag: string) => {
    setPromoteTagsRaw((prev) => {
      const existing = prev
        .split(/[,\n]/)
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
      if (existing.includes(tag.toLowerCase())) return prev;
      return prev.trim() ? `${prev.replace(/[,\s]+$/, "")}, ${tag}` : tag;
    });
  };
  const [helpOpen, setHelpOpen] = useState(false);

  // Below ~1024px the 55/45 two-panel layout gets cramped (the right
  // panel needs ~500-600px for its option tables + map). Collapse to
  // a single primary panel: list when nothing is selected, detail
  // when something is. Picking a row expands the detail; closing
  // returns to the list.
  const isCompact = useIsCompactLayout();

  const asyncOp = useAsyncOperation({
    siteId: pendingOperation?.siteId ?? "",
    operationKey: pendingOperation?.operationKey ?? null,
    // Sync-only: the chunked path runs for 30+ min, well past
    // useAsyncOperation's 15-min client-side timeout. Chunked
    // searches use the operations-list polling below instead.
    enabled: pendingOperation !== null && pendingOperation.kind !== "chunked",
  });

  // For chunked searches, poll the operations endpoint to find this
  // search's row and react to its status / chunksCompleted changes.
  // This is the long-running cousin of useAsyncOperation - same idea,
  // no 15-min timeout, and the data model exposes per-chunk progress
  // rather than per-stage.
  const chunkedOps = useLeadSearchOperations({
    enabled: pendingOperation?.kind === "chunked",
    refetchInterval: pendingOperation?.kind === "chunked" ? 5000 : false,
  });
  const chunkedOpRow = useMemo(() => {
    if (!pendingOperation) return null;
    return (
      chunkedOps.data?.operations.find(
        (op) => op.operationKey === pendingOperation.operationKey
      ) ?? null
    );
  }, [chunkedOps.data, pendingOperation]);

  // When the worker completes (sync path), re-issue the search to
  // pick up the now-cached enriched candidates.
  useEffect(() => {
    if (!pendingOperation) return;
    if (asyncOp.data?.status !== "completed") return;
    // Don't clear pendingOperation here - we want the worker progress
    // banner to stay alive while runSearch's "ready" branch decides
    // whether to auto-fire premium. The ready branch clears it
    // explicitly: it sets premiumAutoFireProgress first (so the same
    // banner can transition from "Step 4 of 5" to "Step 5 of 5"
    // without a visual gap), THEN clears pendingOperation. Without
    // that ordering the banner blinks empty for a moment and then
    // "jumps up" a fresh Step 5 alert.
    runSearch({ forceRefetch: false }).catch((err) => {
      setSearchError(err instanceof Error ? err.message : "Search failed");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncOp.data?.status]);

  // Progressive reveal (cold search): while the worker runs its
  // server-side premium pass (Step 5 of 5), the base passes are already
  // persisted, so /leads/search is a pure read that returns the current
  // rows with premiumEnrichedAt ("$") filling in per row. Poll it so the
  // base results appear at Step 5 and "$" lights up top-to-bottom, rather
  // than everything landing at once on completion. Warm searches already
  // get this via the FE premium fallback loop above; this restores it for
  // cold, server-side-premium runs.
  const premiumStageRunning =
    asyncOp.data?.status === "running" &&
    (asyncOp.data?.progress?.total ?? 0) >= 5 &&
    (asyncOp.data?.progress?.current ?? 0) >= PREMIUM_STAGE_INDEX;
  useEffect(() => {
    if (!pendingOperation || !premiumStageRunning) return;
    let cancelled = false;
    let inFlight = false;
    const peek = async () => {
      if (inFlight) return;
      const target = activeSearchTargetRef.current;
      if (!target) return;
      inFlight = true;
      const areaBody =
        target.kind === "viewport"
          ? { viewportBounds: target.bounds }
          : {
              postcodeOutward: postcodeOutward.trim() || undefined,
              radiusKm: radiusKm ? Number(radiusKm) : undefined,
            };
      try {
        const res = await peekLeadCandidates.mutateAsync({
          ...areaBody,
          minFloorAreaSqm: minFloorAreaSqm
            ? Number(minFloorAreaSqm)
            : undefined,
          forceRefetch: false,
        });
        // Only a completed (base-done) response carries the rows; ignore
        // any other tick, and never run premium here - the worker owns it.
        if (!cancelled && res.status === "completed") {
          setCandidates(res.candidates);
        }
      } catch {
        // Best-effort: a failed peek just skips this tick.
      } finally {
        inFlight = false;
      }
    };
    void peek();
    const id = window.setInterval(peek, PREMIUM_PEEK_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOperation, premiumStageRunning]);

  // Surface sync-worker failures.
  useEffect(() => {
    if (!pendingOperation) return;
    if (asyncOp.data?.status === "failed") {
      setSearchError(asyncOp.data.error ?? "Enrichment failed");
      setPendingOperation(null);
    }
  }, [asyncOp.data, pendingOperation]);

  // Poll the in-flight landlord-research LRO. On completion, capture the AI
  // trace run id, re-run the search to pull the row's freshly-persisted
  // agentic candidates, and clear the op. `useOperation` stops polling once
  // the status is terminal.
  const researchOpPoll = useOperation(
    researchOp?.operationKey ?? null,
    researchOp?.siteId ?? null
  );
  useEffect(() => {
    if (!researchOp) return;
    const op = researchOpPoll.data?.operation;
    if (!op) return;
    if (op.status === "completed") {
      // The trace run_id is persisted onto the row (agenticTraceRunId); the
      // runSearch() below re-pulls the candidate with it, so the panel reads it off
      // the row rather than a session-scoped map.
      const rawResultStatus = op.result?.status;
      const resultStatus =
        typeof rawResultStatus === "string" ? rawResultStatus : null;
      if (resultStatus === "not_configured") {
        setSearchError(
          "Web search isn't configured for this environment yet - the research gateway needs provisioning before landlord research can run."
        );
      }
      setResearchOp(null);
      runSearch({ forceRefetch: false }).catch((err) => {
        setSearchError(err instanceof Error ? err.message : "Search failed");
      });
    } else if (op.status === "failed" || op.status === "cancelled") {
      setSearchError("Landlord research failed - see the operations page.");
      setResearchOp(null);
    }
    // Also key on the attached op's identity so that when a re-hydrated op
    // (navigated away and back, so `researchOp` re-loaded from sessionStorage)
    // re-attaches to a poll whose first resolved status is already terminal,
    // the completion re-run + clear still fire (status alone would not
    // "transition" on re-attach). eslint-disable: researchOpPoll/runSearch are
    // intentionally excluded to avoid re-running on every poll tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [researchOpPoll.data?.operation?.status, researchOp?.operationKey]);

  // Bulk research poll: on completion, re-run the search to pull every row's
  // freshly-persisted candidates, and surface an actionable tail (rows beyond the
  // per-run cap / failures) so the operator knows to run again. A clean run just
  // refreshes silently, like the single-row path.
  const bulkResearchOpPoll = useOperation(
    bulkResearchOp?.operationKey ?? null,
    bulkResearchOp?.siteId ?? null
  );
  // Incremental reveal: the bulk worker researches rows one at a time and bumps
  // progress.current as each finishes. Re-pull the search each time it advances so a
  // finished row's freshly-persisted candidates appear mid-batch (mirrors the chunked
  // path below), instead of every row staying "researching" until the whole batch ends.
  const lastBulkResearchCurrent = useRef<number>(0);
  // Total hard failures across a whole auto-continue chain, so the final message
  // reports every wave's failures, not just the last. Reset when a fresh chain
  // starts (a manual launch or a row's "run again"), accumulated on each
  // auto-continue hop.
  const chainFailedResearchCount = useRef<number>(0);
  useEffect(() => {
    if (!bulkResearchOp) return;
    const op = bulkResearchOpPoll.data?.operation;
    if (!op) return;
    const current = op.progress?.current ?? 0;
    if (
      op.status !== "completed" &&
      current > lastBulkResearchCurrent.current
    ) {
      lastBulkResearchCurrent.current = current;
      runSearch({ forceRefetch: false }).catch((err) => {
        setSearchError(err instanceof Error ? err.message : "Search failed");
      });
    }
    if (op.status === "completed") {
      const result = op.result as BulkResearchResultShape | undefined;
      const inputRefs = bulkResearchOp.inputRefs;
      const cancelled = bulkResearchOp.cancelledRefs;
      setBulkResearchOp(null);
      lastBulkResearchCurrent.current = 0;
      if (result?.status === "not_configured") {
        chainFailedResearchCount.current = 0;
        setSearchError(
          "Web search isn't configured for this environment yet - landlord research can't run."
        );
      } else {
        // Shared decision (see planBulkResearchContinuation): what's left is
        // derived from what actually got done, so rows past the BE per-batch cap
        // aren't silently dropped; the chain auto-continues until none remain.
        const plan = planBulkResearchContinuation(
          result ?? {},
          inputRefs,
          cancelled
        );
        if (plan.action === "continue") {
          // Carry this wave's failures forward so the chain's final message
          // reports the total, not just the last wave's.
          chainFailedResearchCount.current += plan.batchFailedCount;
          void launchBulkResearch(plan.remaining);
        } else {
          const totalFailed =
            chainFailedResearchCount.current + plan.batchFailedCount;
          chainFailedResearchCount.current = 0;
          if (plan.remaining.length > 0) {
            setResearchNotRunRefs((prev) => {
              const next = new Set(prev);
              plan.remaining.forEach((r) => next.add(r));
              return next;
            });
          }
          if (plan.remaining.length > 0 || totalFailed > 0) {
            setSearchError(
              (plan.remaining.length > 0
                ? `${plan.remaining.length} row${plan.remaining.length === 1 ? "" : "s"} could not be researched - use "run again" on those rows.`
                : "") +
                (totalFailed > 0
                  ? `${plan.remaining.length > 0 ? " " : ""}${totalFailed} failed - see the operations page.`
                  : "")
            );
          }
        }
      }
      runSearch({ forceRefetch: false }).catch((err) => {
        setSearchError(err instanceof Error ? err.message : "Search failed");
      });
    } else if (op.status === "failed" || op.status === "cancelled") {
      setSearchError(
        "Bulk landlord research failed - see the operations page."
      );
      setBulkResearchOp(null);
      lastBulkResearchCurrent.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bulkResearchOpPoll.data?.operation?.status,
    bulkResearchOpPoll.data?.operation?.progress?.current,
  ]);

  // Read once for the render: the per-row research chips (left table) and the right panel
  // both derive each row's done / researching / queued state from this single bulk-op
  // poll (progress.current) plus the launch-ordered ref list - no per-row polling.
  const bulkResearchProgress = bulkResearchOpPoll.data?.operation?.progress;
  const bulkResearchCurrent = bulkResearchProgress?.current ?? 0;
  const bulkResearchActiveSearches = bulkResearchProgress?.processed ?? 0;
  const bulkResearchCancelledRefs = bulkResearchOp?.cancelledRefs ?? null;
  // The current sub-batch's overflow: rows selected into the chain that this
  // batch didn't queue and that auto-continue will pick up next. They show
  // "queued" so all chain rows keep a correct chip, not just the running batch.
  const bulkResearchChainPendingRefs = useMemo(() => {
    if (!bulkResearchOp) return null;
    const queued = new Set(bulkResearchOp.refs);
    return new Set(bulkResearchOp.inputRefs.filter((r) => !queued.has(r)));
  }, [bulkResearchOp]);
  // A single right-panel "Research landlord" in flight: the launched op's ref,
  // or (before the op is set) the focused row while the kickoff mutation runs.
  const singleResearchingRef =
    researchOp?.sourceRef ??
    (researchVoaLandlord.isLoading ? selectedSourceRef : null);

  // Chunked path: each time `chunksCompleted` advances, re-run the
  // search to pull the freshly-enriched rows out of Postgres. When
  // the operation terminates (completed / cancelled / failed),
  // clear the pending state.
  const lastChunksCompleted = useRef<number>(0);
  useEffect(() => {
    if (!pendingOperation || pendingOperation.kind !== "chunked") return;
    if (!chunkedOpRow) return;
    if (chunkedOpRow.chunksCompleted > lastChunksCompleted.current) {
      lastChunksCompleted.current = chunkedOpRow.chunksCompleted;
      runSearch({ forceRefetch: false }).catch((err) => {
        setSearchError(err instanceof Error ? err.message : "Search failed");
      });
    }
    if (chunkedOpRow.status === "completed") {
      setPendingOperation(null);
      lastChunksCompleted.current = 0;
    }
    if (chunkedOpRow.status === "cancelled") {
      setPendingOperation(null);
      lastChunksCompleted.current = 0;
    }
    if (chunkedOpRow.status === "failed") {
      setSearchError(chunkedOpRow.error ?? "Search failed");
      setPendingOperation(null);
      lastChunksCompleted.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chunkedOpRow?.chunksCompleted, chunkedOpRow?.status]);

  const visible = useMemo(() => {
    const filtered =
      statusFilter == null
        ? candidates
        : candidates.filter((c) => candidateStatus(c) === statusFilter);
    const out = [...filtered];
    if (sort) {
      const dir = sort.dir === "asc" ? 1 : -1;
      const k = sort.key;
      const valueFor = (c: LeadCandidate) => {
        switch (k) {
          case "address":
            return (c.address ?? "").toLowerCase();
          case "locationConfidence":
            return locationConfidenceForCandidate(c) ?? -1;
          case "matchConfidence":
            return c.matchConfidence ?? -1;
          case "combinedBuildingConfidence":
            return (
              combinedBuildingConfidence(
                locationConfidenceForCandidate(c),
                c.matchConfidence ?? null
              ) ?? -1
            );
          case "overallConfidence":
            return overallConfidenceForCandidate(c) ?? -1;
          case "surfaceAreaSqm":
            return c.buildingSurfaceAreaSqm ?? -1;
          case "tenantConfidence":
            return c.tenantMatches[0]?.confidence ?? -1;
          case "tenantName":
            return (
              c.tenantMatches[0]?.orgName ??
              c.tenantMatches[0]?.companyName ??
              ""
            ).toLowerCase();
          case "leadValue":
            return leadValueSortWeight(c.leadValue);
          case "landlordConfidence":
            return effectiveTopLandlord(c)?.confidence ?? -1;
          case "landlordName": {
            const m = effectiveTopLandlord(c);
            return (m?.orgName ?? m?.proprietorName ?? "").toLowerCase();
          }
          case "status":
          default:
            return candidateStatus(c);
        }
      };
      const addressKey = (c: LeadCandidate) => (c.address ?? "").toLowerCase();
      out.sort((a, b) => {
        const av = valueFor(a);
        const bv = valueFor(b);
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        // Every ranked column here is a rounded percentage or a five-value
        // band, so ties are the common case rather than the exception, and
        // without a final rule the order inside a tie is whatever the search
        // returned - which can differ between reads and shuffles rows the
        // operator is working through. Address is the one key on the row that
        // is total and stable. Same final rule the inbox applies (its
        // `ADDRESS_TIE_BREAK`), so the two boards break ties alike; the two
        // implementations differ only because this page's sort is a single
        // URL-backed key while the inbox's is a rule list.
        //
        // Always ASCENDING, never mirrored by `dir`: this is a stable ordering,
        // not a second thing the operator chose to rank on. Skipped when
        // address is itself the sort, where it would be a no-op.
        if (k === "address") return 0;
        const ax = addressKey(a);
        const bx = addressKey(b);
        return ax < bx ? -1 : ax > bx ? 1 : 0;
      });
    }
    return out;
  }, [candidates, sort, statusFilter]);

  const selectedCandidate =
    visible.find((c) => c.sourceRef === selectedSourceRef) ?? null;

  // The open lead's state within an in-flight bulk batch (done / researching / queued /
  // cancelled / null), so the right panel can read "Queued" vs "Researching" and offer
  // Cancel only while it's still queued.
  // Must go through researchRowChipState - the SAME wrapper the results cell
  // uses - not the bare bulkResearchRowStateForRef underneath it. The bare
  // helper only sees `bulkResearchOp.refs` (the refs the BE queued for the
  // CURRENT sub-batch), so a row appended to the chain by a single-row click
  // lands in `inputRefs` only: the table read "queued" off chainPendingRefs
  // while the panel button still offered "Research landlord".
  const selectedBulkResearchState = selectedCandidate
    ? researchRowChipState({
        ref: selectedCandidate.sourceRef,
        singleResearchingRef,
        bulkRefs: bulkResearchOp?.refs,
        bulkCurrent: bulkResearchCurrent,
        bulkCancelledRefs: bulkResearchCancelledRefs,
        chainPendingRefs: bulkResearchChainPendingRefs,
        serverOperationKey: selectedCandidate.researchOperationKey,
      })
    : null;

  // The building a row is currently resolved to. Same precedence the
  // "Building (m2)" cell renders and `buildPromoteSelection` persists, so
  // "these two rows are the same building" means the same thing everywhere.
  const effectiveBuildingBkey = (c: LeadCandidate): string | null => {
    const pending = pendingSelectionByRef[c.sourceRef];
    return (
      pending?.buildingBkey ??
      c.selectedBuildingBkey ??
      c.buildingCandidates?.[0]?.bkey ??
      null
    );
  };

  // Rows that resolve to a building ALREADY claimed by a row above them in the
  // current list order. VOA lists several rateable units per physical building
  // (a depot can appear three times under different unit addresses), and each
  // resolves to the same polygon - and therefore the same OSM tenant, since
  // tenant lookup is anchored on the building. Marking the later rows keeps the
  // operator from promoting one building as three leads. Keyed off `visible`,
  // so it follows whatever sort/filter is applied rather than a fixed ranking.
  const duplicateBuildingByRef = useMemo(() => {
    const firstForBkey = new Map<string, LeadCandidate>();
    const dupes = new Map<string, LeadCandidate>();
    for (const c of visible) {
      const bkey = effectiveBuildingBkey(c);
      if (!bkey) continue;
      const first = firstForBkey.get(bkey);
      if (first) dupes.set(c.sourceRef, first);
      else firstForBkey.set(bkey, c);
    }
    return dupes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, pendingSelectionByRef]);

  const pins: OverviewPin[] = useMemo(
    () =>
      visible
        // Keep a lead if it has ANY plottable position - the resolved
        // building OR the geocode point. Must mirror the lat/lon fallback
        // below: filtering on c.latitude alone dropped leads whose geocode
        // came back null (e.g. a partial enrichment failure) even though
        // their building resolved, so they vanished from the map while the
        // detail view still drew the building outline.
        .filter(
          (c) =>
            (c.buildingLatitude ?? c.latitude) != null &&
            (c.buildingLongitude ?? c.longitude) != null
        )
        .map((c) => ({
          id: c.sourceRef,
          lat: (c.buildingLatitude ?? c.latitude) as number,
          lon: (c.buildingLongitude ?? c.longitude) as number,
          label: c.address ?? c.sourceRef,
          // Drives the overview map's located-vs-approximate rendering:
          // present => a resolved building footprint (orange outline),
          // null => postcode-only lead (small blue dot).
          polygon: c.buildingPolygon,
        })),
    [visible]
  );

  // The site the search was anchored on ("Near this site"), plotted as a
  // reference marker so the operator can see where they searched from. Null
  // when no site is selected or it has no coordinates.
  // The proximity anchor a lead promoted from THIS search would carry. The
  // persisted `proximityContext` only exists once a row is promoted, so the
  // triage pane synthesises the live equivalent from the selected "Near this
  // site" anchor and shows the same <ProximityChip> the /leads inbox uses -
  // the operator sees the proximity before deciding to promote, not after.
  // `capturedAt`/`capturedBy` stay null: nothing has been captured yet.
  const searchProximityContext = useMemo<ProximityContext | null>(() => {
    if (!selectedSite) return null;
    const lat =
      selectedCandidate?.buildingLatitude ?? selectedCandidate?.latitude;
    const lon =
      selectedCandidate?.buildingLongitude ?? selectedCandidate?.longitude;
    // Distance needs coordinates at BOTH ends; without them the chip still
    // shows "near <site>" with no distance. Mirrors the BE, which stamps a
    // context off the anchor site whether or not it has coordinates - so the
    // preview can't promise a chip the promoted lead won't have, or vice versa.
    const measurable =
      selectedSite.lat != null &&
      selectedSite.lon != null &&
      lat != null &&
      lon != null;
    return {
      anchorSiteId: selectedSite.value,
      anchorSiteName: selectedSite.name,
      distanceMetres: measurable
        ? haversineMetres(
            selectedSite.lat as number,
            selectedSite.lon as number,
            lat as number,
            lon as number
          )
        : null,
      operationalAtCapture: selectedSite.operational,
      capturedBy: null,
      capturedAt: null,
    };
  }, [selectedSite, selectedCandidate]);

  const anchorSite = useMemo(
    () =>
      selectedSite && selectedSite.lat != null && selectedSite.lon != null
        ? {
            lat: selectedSite.lat,
            lon: selectedSite.lon,
            name: selectedSite.name,
          }
        : null,
    [selectedSite]
  );

  const runSearch = async (
    opts: {
      forceRefetch?: boolean;
      confirmEnrichment?: boolean;
      // Set only when *initiating* a new search. Re-issues (preview-confirm,
      // async poll) omit it and reuse the target held in `searchTarget`.
      target?:
        | { kind: "postcode" }
        | { kind: "viewport"; bounds: LeadSearchViewportBounds };
    } = {}
  ) => {
    setSearchError(null);
    const forceRefetch = opts.forceRefetch ?? false;
    const confirmEnrichment = opts.confirmEnrichment ?? false;
    // Re-issues omit opts.target and reuse the ref (see activeSearchTargetRef
    // doc) - reading state here would be stale in the synchronous auto-confirm
    // recursion below.
    const target = opts.target ?? activeSearchTargetRef.current;
    if (opts.target) {
      activeSearchTargetRef.current = opts.target;
      setSearchTarget(opts.target);
      // Initiating a new search: clear the previous search's results
      // immediately so they don't sit under the new-search progress bar
      // looking like the new results (the filter counts + rows are the
      // OLD area until this one finishes). They're repopulated on
      // completion - the sync "completed" branch below, or the async-poll
      // re-issue (which omits opts.target, so it doesn't re-clear).
      setCandidates([]);
      setSelectedRowRefs(new Set());
      setResultMeta(null);
    }
    // Floor filters carry over to a viewport search (agreed UX: same
    // warehouse leads, new area). The area itself comes from the viewport
    // bounds; postcode/radius are omitted so the BE takes the viewport path.
    const areaBody =
      target.kind === "viewport"
        ? { viewportBounds: target.bounds }
        : {
            postcodeOutward: postcodeOutward.trim() || undefined,
            radiusKm: radiusKm ? Number(radiusKm) : undefined,
          };
    try {
      const res = await searchLeadCandidates.mutateAsync({
        ...areaBody,
        minFloorAreaSqm: minFloorAreaSqm ? Number(minFloorAreaSqm) : undefined,
        forceRefetch: forceRefetch || undefined,
        confirmEnrichment: confirmEnrichment || undefined,
      });
      // Fit the map to results only for a typed-postcode search. A viewport
      // search must leave the operator's pan/zoom untouched.
      if (res.status === "completed" && target.kind === "postcode") {
        // Drop the remembered viewport so that if this search completed while
        // the map was unmounted (a lead open), remounting fits to the new
        // results instead of restoring the stale pre-search view.
        overviewViewportRef.current = null;
        setMapFitToken((t) => t + 1);
      }
      // Any completed search means the map now reflects the results - hide
      // the "Search this area" button until the operator moves the map.
      if (res.status === "completed") {
        setMapSearchToken((t) => t + 1);
      }
      if (res.status === "completed") {
        setCandidates(res.candidates);
        setSelectedRowRefs(new Set());
        setPendingPreview(null);
        setResultMeta({
          totalMatched: res.totalMatched,
          totalReturned: res.totalReturned,
          truncated: res.truncated,
          failedRowCount: res.failedRowCount ?? 0,
          failedRowRefs: res.failedRowRefs ?? [],
        });
        // pendingOperation is cleared AFTER the premium auto-fire
        // decision below so the progress banner can transition from
        // "Step 4 of 5" to "Step 5 of 5" without blinking empty in
        // between. Without this ordering the worker banner unmounts
        // before the premium banner mounts and the operator sees a
        // visual gap + a "Step 5 of 5" alert jumping up afresh.
        if (res.candidates.length < PREMIUM_ENRICH_PROMPT_THRESHOLD) {
          // Fallback premium enrichment for small searches. The
          // lead-search worker now runs premium server-side as stage 5
          // (operation_lead_search.py) for cold searches, so those rows
          // arrive with `premiumEnrichedAt` already set and `eligibleRefs`
          // below is empty - this loop then no-ops. It still fires for
          // the warm-base case (all four base passes cached, so no worker
          // ran) and for any row the worker left un-premium. Cost is
          // bounded by the <100 row gate (~$1/search worst case) so a
          // confirm-modal click was just friction. The mutation merges
          // refreshed candidates back into state when it returns;
          // `premiumEnrichVoaCandidates.isLoading` keeps the per-row +
          // bulk Enrich buttons disabled while it runs.
          //
          // Chunked sequentially: the BE endpoint runs synchronously
          // inside an API Gateway request (29s ceiling). Premium pass
          // is ~2-3s/row, so a batch of N=5 keeps each round-trip
          // ~15s with cold-start headroom. Larger calls hit a 503
          // timeout - exactly what we saw on a 29-row search before
          // the chunking landed. Errors on one batch don't abort the
          // rest; we aggregate failures into a single end-of-run
          // error message so partial success still ships the $
          // adorners that did succeed.
          const eligibleRefs = res.candidates
            .filter((c) => c.premiumEnrichedAt == null)
            .map((c) => c.sourceRef);
          if (eligibleRefs.length === 0) {
            // No premium-eligible rows (all already enriched). Worker
            // is done and there's nothing else to run; clear the banner.
            setPendingOperation(null);
          } else {
            const totalBatches = Math.ceil(
              eligibleRefs.length / PREMIUM_AUTO_FIRE_BATCH_SIZE
            );
            // Set premium banner FIRST so the unified progress block
            // can re-render with "Step 5 of 5" before we retire the
            // worker pendingOperation. Without this ordering the
            // banner blinks empty for a frame and the operator sees a
            // fresh "Step 5 of 5" alert jumping up.
            setPremiumAutoFireProgress({
              done: 0,
              total: totalBatches,
              eligibleRefs: eligibleRefs.length,
            });
            setPendingOperation(null);
            void (async () => {
              const failedBatchErrors: string[] = [];
              let enrichedCount = 0;
              for (
                let i = 0;
                i < eligibleRefs.length;
                i += PREMIUM_AUTO_FIRE_BATCH_SIZE
              ) {
                const chunk = eligibleRefs.slice(
                  i,
                  i + PREMIUM_AUTO_FIRE_BATCH_SIZE
                );
                // Strict predicate: premium enrich bills Google Places per row
                // and writes the VOA rows, so a 504 / client timeout that may
                // still be executing must never be retried into a duplicate
                // concurrent run.
                const outcome = await runBatchWithRetry(
                  () =>
                    premiumEnrichVoaCandidates.mutateAsync({
                      sourceRefs: chunk,
                    }),
                  isNotYetExecutedHttpError
                );
                const refreshed = outcome.ok ? outcome.value.candidates : null;
                const lastErr = outcome.ok ? null : outcome.error;
                if (refreshed !== null) {
                  enrichedCount += refreshed.length;
                  const refreshedByRef = new Map(
                    refreshed.map((c) => [c.sourceRef, c])
                  );
                  setCandidates((prev) =>
                    prev.map((c) => refreshedByRef.get(c.sourceRef) ?? c)
                  );
                } else {
                  failedBatchErrors.push(
                    lastErr instanceof Error ? lastErr.message : String(lastErr)
                  );
                }
                setPremiumAutoFireProgress((prev) =>
                  prev
                    ? {
                        ...prev,
                        done: Math.floor(i / PREMIUM_AUTO_FIRE_BATCH_SIZE) + 1,
                      }
                    : prev
                );
              }
              setPremiumAutoFireProgress(null);
              if (failedBatchErrors.length > 0) {
                setSearchError(
                  `Auto premium enrichment: ${enrichedCount} of ` +
                    `${eligibleRefs.length} rows enriched, ` +
                    `${failedBatchErrors.length} batch${failedBatchErrors.length === 1 ? "" : "es"} failed. ` +
                    `Click the per-row "Enrich" button or use "Force refresh" to retry. ` +
                    `Last error: ${failedBatchErrors[failedBatchErrors.length - 1]}`
                );
              }
            })();
          }
        } else {
          // No premium auto-fire path (>=100 candidates). Clear the
          // worker banner now that the worker is done.
          setPendingOperation(null);
        }
        return;
      }
      if (res.status === "preview") {
        // Backend says this search would hit external APIs. For
        // short searches (sub-10s ETA) skip the modal and re-issue
        // straight away - the wait isn't long enough to justify the
        // extra click. Otherwise pop the confirm modal so the
        // operator can opt-out before we pay for them. The candidate
        // table is left untouched - the operator's deciding whether
        // to enrich, not whether to re-render.
        const etaSeconds = estimateCheapSearchSeconds(
          res.totalPendingEnrichment
        );
        if (etaSeconds <= AUTO_CONFIRM_ETA_SECONDS) {
          await runSearch({ forceRefetch, confirmEnrichment: true });
          return;
        }
        setPendingPreview({
          totalMatched: res.totalMatched,
          totalReturned: res.totalReturned,
          truncated: res.truncated,
          totalPendingEnrichment: res.totalPendingEnrichment,
          forceRefetch: res.forceRefetch,
        });
        return;
      }
      // Pending: kick off polling. We keep the previous candidates on
      // screen while the worker runs - they're whatever was in cache
      // last time round; the operator can still scroll / click while
      // enrichment finishes.
      setPendingOperation({
        operationKey: res.operationKey,
        siteId: res.siteId,
        totalMatched: res.totalMatched,
        totalReturned: res.totalReturned,
        truncated: res.truncated,
        totalPendingEnrichment: res.totalPendingEnrichment,
        forceRefetch,
        kind: res.kind ?? "sync",
      });
      setPendingPreview(null);
      setResultMeta({
        totalMatched: res.totalMatched,
        totalReturned: res.totalReturned,
        truncated: res.truncated,
      });
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : "Search failed");
    }
  };
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    // The top Search button re-runs the *active* target with the current
    // filters. After a "Search this area" it re-searches the same viewport
    // (so changing Min area / radius and hitting Search re-runs the panned
    // area, not the typed postcode). Editing the postcode box flips the
    // active target back to postcode (see the postcode input onChange).
    const target = activeSearchTargetRef.current;
    if (target.kind === "viewport") {
      await runSearch({ target });
      return;
    }
    if (!postcodeOutward.trim()) {
      setSearchError("Select a site to search near before searching.");
      return;
    }
    await runSearch({ target: { kind: "postcode" } });
  };
  // "Search this area" on the overview map: search the outcodes the current
  // viewport covers, keeping the active floor filters. The map is left where
  // the operator panned (runSearch only fits on a postcode search).
  const handleSearchArea = async (bounds: LeadSearchViewportBounds) => {
    await runSearch({ target: { kind: "viewport", bounds } });
  };
  // Mirrors `CHUNKED_SEARCH_THRESHOLD` on the BE
  // (lambda_handlers/api/admin/leads.py). Searches matching more
  // than this many rows go through the chunked path, which means
  // they show up on /leads/search/status and can be cancelled.
  // Smaller searches use the sync long-running Lambda; they're not
  // cancellable and finish in under 15 min. Surfacing the kind in
  // the pre-search modal so the operator knows what they're
  // committing to.
  const CHUNKED_SEARCH_THRESHOLD = 2_500;

  const handleConfirmEnrichment = async () => {
    if (!pendingPreview) return;
    const force = pendingPreview.forceRefetch;
    setPendingPreview(null);
    await runSearch({ forceRefetch: force, confirmEnrichment: true });
  };
  const handleCancelEnrichment = () => {
    setPendingPreview(null);
  };

  // Advance to whatever's immediately next in the visible (sorted +
  // filtered) list. Respects the operator's status filter: if they
  // want only New rows in scope, they can toggle that filter and the
  // next click stays in the same bucket. Previously this was
  // hard-coded to skip past anything that wasn't "new", which
  // diverged from the visible sort order whenever the table mixed
  // statuses (e.g. sorting by confidence with the All filter on).
  const findNextNew = (afterSourceRef: string): string | null => {
    const idx = visible.findIndex((c) => c.sourceRef === afterSourceRef);
    if (idx === -1) return null;
    if (idx + 1 >= visible.length) return null;
    return visible[idx + 1].sourceRef;
  };

  const applyReviewLocally = (
    sourceRef: string,
    patch: Partial<LeadCandidate>
  ) => {
    setCandidates((prev) =>
      prev.map((c) => (c.sourceRef === sourceRef ? { ...c, ...patch } : c))
    );
  };

  const handleSave = async (payload: SavePayload, andNext: boolean) => {
    if (!selectedCandidate) return;
    const ref = selectedCandidate.sourceRef;
    const review = await reviewVoaCandidate.mutateAsync({
      sourceRef: ref,
      selectedBuildingBkey: payload.buildingBkey,
      selectedLandlord: payload.landlord,
      selectedTenant: payload.tenant,
      ownerOccupier: payload.ownerOccupier,
      hasPv: payload.hasPv,
    });
    applyReviewLocally(ref, {
      selectedBuildingBkey: payload.buildingBkey,
      selectedLandlord: payload.landlord,
      selectedTenant: payload.tenant,
      ownerOccupier: payload.ownerOccupier,
      hasPv: payload.hasPv,
      reviewedAt: review.reviewedAt,
      reviewedBy: review.reviewedBy,
      rejectedAt: null,
      rejectedBy: null,
    });
    // The saved selection is now the canonical truth - drop the
    // pending shadow so the table cell falls back to it instead of
    // showing "unsaved" when the operator returns to the row.
    setPendingSelectionByRef((prev) => {
      const next = { ...prev };
      delete next[ref];
      return next;
    });
    if (andNext) {
      const next = findNextNew(ref);
      setSelectedSourceRef(next);
    }
  };

  // Cache the panel's mid-edit payload at the moment Promote & next
  // is clicked. The modal uses these as the selection payload when
  // the operator confirms with their tags. Without this we'd lose
  // the selections by the time the modal closes.
  const [singlePromoteSelections, setSinglePromoteSelections] =
    useState<SavePayload | null>(null);

  // Tags applied to leads we created in this session, keyed by
  // sourceRef. Surfaced in the Status cell after the Lead chip so
  // the operator can see what they tagged a row with without
  // navigating to /leads. Only populated for rows promoted in the
  // current session - already-a-lead rows that existed before the
  // session don't carry tags here (we'd need a /leads cross-lookup).
  const [createdLeadTagsByRef, setCreatedLeadTagsByRef] = useState<
    Record<string, string[]>
  >({});

  // Promoting a row whose building ALREADY has a lead makes two leads for one
  // physical site, so name the other lead's address and let the operator
  // decide. Every conflict in a bulk promote is gathered into ONE prompt -
  // never one dialog per row. Returns false when the operator backs out.
  const confirmNoDuplicateBuildingLeads = (refs: string[]): boolean => {
    const conflicts: string[] = [];
    const claimedInThisBatch = new Map<string, LeadCandidate>();
    for (const ref of refs) {
      const c = candidates.find((x) => x.sourceRef === ref);
      if (!c) continue;
      const bkey = effectiveBuildingBkey(c);
      if (!bkey) continue;
      const otherLead = candidates.find(
        (x) =>
          x.sourceRef !== ref &&
          x.existingLeadId != null &&
          effectiveBuildingBkey(x) === bkey
      );
      // Two rows promoted together are just as duplicative as one promoted
      // against a pre-existing lead - and a bulk promote is exactly where the
      // operator can't see the overlap themselves.
      const earlierInBatch = claimedInThisBatch.get(bkey);
      const clash = otherLead ?? earlierInBatch;
      if (clash) {
        conflicts.push(
          `"${c.address ?? c.sourceRef}" - same building as "${clash.address ?? clash.sourceRef}"${otherLead ? ", which is already a lead" : ", also being promoted now"}`
        );
      }
      if (!claimedInThisBatch.has(bkey)) claimedInThisBatch.set(bkey, c);
    }
    if (conflicts.length === 0) return true;
    const anyExistingLead = conflicts.some((c) => c.includes("already a lead"));
    const headline = anyExistingLead
      ? "the same building as a lead you already have"
      : "the same building as another row in this promote";
    return window.confirm(
      `${conflicts.length === 1 ? "This row appears to be" : `These ${conflicts.length} rows appear to be`} ${headline}:\n\n` +
        conflicts.map((c) => `  - ${c}`).join("\n") +
        `\n\nPromoting would create a second lead for the same site. Promote anyway?`
    );
  };

  // One-shot promote of a single candidate using the panel's mid-edit
  // selections. Shared by both promote-and-next entry points (the
  // no-tag direct path and the tag-modal confirm path) so the BE call
  // shape + the local-state cleanup + the advance-to-next-New behaviour
  // stay in one place.
  const executeSinglePromote = async (
    ref: string,
    payload: SavePayload,
    tags: string[] | undefined
  ) => {
    if (!confirmNoDuplicateBuildingLeads([ref])) return;
    const res = await promoteVoaCandidates.mutateAsync({
      sourceRefs: [ref],
      nearSiteId: nearSiteId || undefined,
      tags: tags && tags.length ? tags : undefined,
      selections: {
        [ref]: {
          selectedBuildingBkey: payload.buildingBkey,
          selectedLandlord: payload.landlord,
          selectedTenant: payload.tenant,
          ownerOccupier: payload.ownerOccupier,
          hasPv: payload.hasPv,
        },
      },
    });
    const created = res.created.find((lead) => lead.sourceRef === ref);
    // A promoted row has had the panel's picks persisted (they are the `selections`
    // above - the BE writes them onto the row before creating the lead), so it has to
    // stop reporting them as unsaved. Save & next has always done this via
    // `applyReviewLocally`; promote didn't, so the panel stayed dirty and the advance
    // to the next row tripped its own "You have unsaved changes on this lead. Discard
    // and leave?" prompt. Gated on `created`, because a SKIPPED row (already a lead,
    // or not found) is skipped before the BE applies any selections - marking those
    // saved would claim a write that never happened.
    //
    // flushSync so the panel has re-registered its navigation blocker as "clean"
    // BEFORE `setSelectedSourceRef` below navigates: batched, the router consults the
    // blocker registered by the previous render and prompts anyway. The panel flushes
    // `savingNavigation` around its own "and next" actions for the same reason - and
    // this path needs its own, because the tag-modal confirm calls straight in here
    // without going through the panel at all.
    if (created) {
      flushSync(() => {
        setCandidates((prev) =>
          prev.map((c) =>
            c.sourceRef === ref
              ? {
                  ...c,
                  existingLeadId: created.id,
                  selectedBuildingBkey: payload.buildingBkey,
                  selectedLandlord: payload.landlord,
                  selectedTenant: payload.tenant,
                  ownerOccupier: payload.ownerOccupier,
                }
              : c
          )
        );
      });
    }
    if (created) {
      setCreatedLeadTagsByRef((prev) => ({
        ...prev,
        [ref]: created.tags ?? [],
      }));
    }
    setPendingSelectionByRef((prev) => {
      const next = { ...prev };
      delete next[ref];
      return next;
    });
    if (res.skipped && res.skipped.length > 0) {
      setSearchError(
        `Promote skipped: ` +
          res.skipped
            .map((s) => `${PROMOTE_SKIP_LABELS[s.reason] ?? s.reason}`)
            .join(", ")
      );
      return;
    }
    const next = findNextNew(ref);
    setSelectedSourceRef(next);
  };

  // No-tag-prompt promote: fires the promote API straight away with
  // the panel's current selections and advances. Default action for
  // the operator who's churning through rows where the default tag
  // set is fine.
  const handlePromoteAndNext = async (payload: SavePayload) => {
    if (!selectedCandidate) return;
    await executeSinglePromote(selectedCandidate.sourceRef, payload, undefined);
  };

  // With-tag-prompt variant: opens the same Promote modal the bulk
  // button uses. Use this when the operator wants to attach tags to
  // the lead before it's created. The modal's confirm calls
  // executeSinglePromote with the tags the operator typed.
  const handlePromoteWithTagAndNext = async (payload: SavePayload) => {
    if (!selectedCandidate) return;
    setSinglePromoteRef(selectedCandidate.sourceRef);
    setSinglePromoteSelections(payload);
    setPromoteModalOpen(true);
  };

  const handleReject = async () => {
    if (!selectedCandidate) return;
    const ref = selectedCandidate.sourceRef;
    const reject = await rejectVoaCandidate.mutateAsync(ref);
    applyReviewLocally(ref, {
      rejectedAt: reject.rejectedAt,
      rejectedBy: reject.rejectedBy,
      reviewedAt: null,
      reviewedBy: null,
    });
    // Advance to the next New row, mirroring Save & next. Falls back
    // to clearing the selection if there's nothing left to review.
    const next = findNextNew(ref);
    setSelectedSourceRef(next);
  };

  const handleRefetch = async () => {
    if (!selectedCandidate) return;
    const ref = selectedCandidate.sourceRef;
    const { candidate } = await refetchVoaCandidate.mutateAsync(ref);
    // Splice the freshly-enriched candidate back into local state so
    // the panel + map react to the new data.
    setCandidates((prev) =>
      prev.map((c) => (c.sourceRef === ref ? candidate : c))
    );
  };

  // Bulk force-refetch on the selected rows, replacing the older
  // top-level "Search & force refetch data" button. Per-row API call
  // (the same one the right panel's Refetch button uses) loop'd over
  // the selection with a small concurrency cap so a 50-row refetch
  // doesn't hold an open HTTP connection per row. Tracks progress as
  // an "X / N done" counter so the operator can see how it's going.
  const REFETCH_CONCURRENCY = 4;
  const [bulkRefetchProgress, setBulkRefetchProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);

  // Bulk retry of just the degraded sources across all visible rows flagged with a
  // non-empty `enrichmentDegradedSources`. One LRO for the whole set: the BE work is
  // upstream-bound (one Overpass call per row, Companies House rate-limited per
  // postcode) and was timing out at the 29s API Gateway ceiling on a SINGLE row, so the
  // client-side chunking this replaces could never bound it - the failing unit was
  // already one row. The worker reports per-row progress; on completion we merge its
  // refreshed rows into the results.
  const [refetchDegradedOp, setRefetchDegradedOp] =
    useState<PersistedRefetchDegradedOp | null>(() => {
      // The shared loader validates the two base fields; `total` is this handle's own,
      // so check it here rather than resume a poll that would label itself "0/undefined".
      const stored = loadPersistedResearchOp<PersistedRefetchDegradedOp>(
        REFETCH_DEGRADED_OP_STORAGE_KEY
      );
      return stored && typeof stored.total === "number" ? stored : null;
    });
  useEffect(() => {
    savePersistedResearchOp(REFETCH_DEGRADED_OP_STORAGE_KEY, refetchDegradedOp);
  }, [refetchDegradedOp]);
  const handleRefetchDegraded = async () => {
    const refs = visible
      .filter((c) => c.enrichmentDegradedSources.length > 0)
      .map((c) => c.sourceRef);
    if (refs.length === 0) return;
    setSearchError(null);
    try {
      const response = await refetchVoaDegradedSources.mutateAsync(refs);
      setRefetchDegradedOp({
        operationKey: response.operationKey,
        siteId: response.siteId,
        total: response.rowCount,
        overflow: response.overflowCount ?? 0,
      });
    } catch (err) {
      setSearchError(
        err instanceof Error
          ? err.message
          : "Retrying the missing data failed to start."
      );
    }
  };
  const refetchDegradedPoll = useOperation(
    refetchDegradedOp?.operationKey ?? null,
    refetchDegradedOp?.siteId ?? null
  );
  // Button label state: in flight from the moment the kickoff mutation fires (before
  // the op key exists), so the operator can't double-click into a second paid retry.
  const refetchDegradedProgress = refetchVoaDegradedSources.isLoading
    ? { done: 0, total: 0 }
    : refetchDegradedOp
      ? {
          done: refetchDegradedPoll.data?.operation?.progress?.current ?? 0,
          total:
            refetchDegradedPoll.data?.operation?.progress?.total ??
            refetchDegradedOp.total,
        }
      : null;
  useEffect(() => {
    if (!refetchDegradedOp) return;
    const op = refetchDegradedPoll.data?.operation;
    if (!op) return;
    if (op.status === "completed") {
      const result = op.result as unknown as
        | VoaRefetchDegradedOperationResult
        | undefined;
      // Merge the refreshed rows in place rather than re-running the search: the
      // operator's scroll position, selection and open panel all survive, and the
      // worker already returned every requested row.
      const refreshed = result?.candidates ?? [];
      if (refreshed.length > 0) {
        const refreshedByRef = new Map(refreshed.map((c) => [c.sourceRef, c]));
        setCandidates((prev) =>
          prev.map((c) => refreshedByRef.get(c.sourceRef) ?? c)
        );
      }
      const stillDegraded = result?.stillDegraded?.length ?? 0;
      const total = result?.total ?? refetchDegradedOp.total;
      const overflow = refetchDegradedOp.overflow;
      setRefetchDegradedOp(null);
      // Both tails are actionable and both are the operator's to act on, so say either
      // that applies rather than only the first.
      const messages = [
        stillDegraded > 0
          ? `${stillDegraded} of ${total} row${total === 1 ? "" : "s"} still have missing data after retry - the upstream may still be unavailable. Try again in a few minutes.`
          : "",
        overflow > 0
          ? `${overflow} more flagged row${overflow === 1 ? "" : "s"} were not retried (one batch at a time) - click again for the rest.`
          : "",
      ].filter(Boolean);
      if (messages.length > 0) setSearchError(messages.join(" "));
    } else if (op.status === "failed" || op.status === "cancelled") {
      setSearchError(
        "Retrying the missing data failed - see the operations page."
      );
      setRefetchDegradedOp(null);
    }
    // Keyed on the op identity as well as the status so a re-attached poll whose first
    // resolved status is already terminal still completes (mirrors the research polls).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    refetchDegradedPoll.data?.operation?.status,
    refetchDegradedOp?.operationKey,
  ]);

  const handleBulkRefetch = async () => {
    const refs = visible
      .filter((c) => selectedRowRefs.has(c.sourceRef))
      .map((c) => c.sourceRef);
    if (refs.length === 0) return;
    setBulkRefetchProgress({ done: 0, total: refs.length });
    setSearchError(null);
    const refreshedByRef = new Map<string, LeadCandidate>();
    let nextIdx = 0;
    let finishedCount = 0;
    const worker = async (): Promise<void> => {
      while (nextIdx < refs.length) {
        const i = nextIdx++;
        if (i >= refs.length) return;
        const ref = refs[i];
        try {
          const { candidate } = await refetchVoaCandidate.mutateAsync(ref);
          refreshedByRef.set(ref, candidate);
        } catch (err) {
          // Don't abort the batch on a single-row failure - record it
          // and let the others through. Surface to the operator at
          // the end via searchError.
          console.warn(`Bulk refetch failed for ${ref}:`, err);
        }
        finishedCount += 1;
        setBulkRefetchProgress({ done: finishedCount, total: refs.length });
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(REFETCH_CONCURRENCY, refs.length) }, worker)
    );
    setCandidates((prev) =>
      prev.map((c) => refreshedByRef.get(c.sourceRef) ?? c)
    );
    const failedCount = refs.length - refreshedByRef.size;
    if (failedCount > 0) {
      setSearchError(
        `Force-refresh failed on ${failedCount} of ${refs.length} row${refs.length === 1 ? "" : "s"}. The rest were re-fetched successfully.`
      );
    }
    setBulkRefetchProgress(null);
  };

  const handlePremiumEnrich = async () => {
    if (!selectedCandidate) return;
    const ref = selectedCandidate.sourceRef;
    const { candidates: refreshed } =
      await premiumEnrichVoaCandidates.mutateAsync({ sourceRefs: [ref] });
    const refreshedByRef = new Map(refreshed.map((c) => [c.sourceRef, c]));
    setCandidates((prev) =>
      prev.map((c) => refreshedByRef.get(c.sourceRef) ?? c)
    );
  };

  // Kick off the agentic landlord-research LRO for the focused row. The
  // poll effect above watches researchOp and re-runs the search when the
  // worker finishes, so the researched candidates appear in the panel.
  const handleResearchLandlord = async () => {
    if (!selectedCandidate) return;
    const ref = selectedCandidate.sourceRef;
    setSearchError(null);
    // Researching this row clears any stale "not run" marker on it.
    setResearchNotRunRefs((prev) => {
      if (!prev.has(ref)) return prev;
      const next = new Set(prev);
      next.delete(ref);
      return next;
    });
    // A single-row click IS a one-row bulk run. Going through the same
    // launcher means sequential clicks inherit everything the bulk path
    // already does - FIFO chaining, the per-row done/researching/queued chips
    // off the worker cursor, per-row cancel, progress and the completion
    // re-pull - instead of a second queue beside it that would drift.
    if (bulkResearchOp) {
      // A chain is already running: append to it. `bulkResearchChainPendingRefs`
      // is inputRefs minus the queued set, so the row immediately reads
      // "queued", and auto-continue picks it up when the current batch lands.
      setBulkResearchOp((prev) =>
        prev && !prev.inputRefs.includes(ref)
          ? { ...prev, inputRefs: [...prev.inputRefs, ref] }
          : prev
      );
      return;
    }
    await launchBulkResearch([ref]);
  };

  // Multi-row premium enrich: invoked from the bulk-action bar above
  // the table when the operator has selected one or more checkboxes.
  // Skips already-premium rows server-side so a mixed selection is
  // safe; the per-row cost estimate in the modal counts only the rows
  // that would actually be paid for.
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);
  const premiumEligible = visible.filter(
    (c) => selectedRowRefs.has(c.sourceRef) && c.premiumEnrichedAt == null
  );
  // Rows the bulk landlord research will run: every selected row. Re-researching a
  // row that already has a landlord is an explicit, paid operator choice - the BE
  // re-checks existence + caps, but does not skip already-researched rows.
  const researchSelected = visible.filter((c) =>
    selectedRowRefs.has(c.sourceRef)
  );
  // Launch one bulk-research sub-batch for `refs` (no confirm). Used by the
  // toolbar action (after its confirm), by auto-continue (the completion effect
  // re-launches with the leftover), and by a row's "run again". `refs` is the
  // remaining chain for this batch; the BE caps it and returns queuedRefs, the
  // rest fold into the auto-continue chain. The caller guards against launching
  // over an in-flight op.
  const launchBulkResearch = async (refs: string[]) => {
    if (refs.length === 0) return;
    // These refs are being (re-)researched, so drop any stale "not run" markers.
    setResearchNotRunRefs((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(prev);
      refs.forEach((r) => next.delete(r));
      return next;
    });
    setSearchError(null);
    try {
      const res = await bulkResearchVoaLandlords.mutateAsync(refs);
      if (res.status === "noop") {
        setSearchError("None of the selected rows are available to research.");
        return;
      }
      if (res.operationKey && res.siteId) {
        setBulkResearchOp({
          operationKey: res.operationKey,
          siteId: res.siteId,
          // Only the rows the BE actually queued (capped/eligible-filtered) are
          // shown "researching"; the overflow rows fold into the chain (shown
          // "queued"). Fall back to the full input if the BE is older.
          refs: res.queuedRefs ?? refs,
          inputRefs: refs,
          cancelledRefs: new Set(),
        });
      }
    } catch {
      setSearchError("Could not start bulk landlord research. Try again.");
    }
  };
  const handleBulkResearch = async () => {
    if (researchSelected.length === 0 || bulkResearchOp !== null) {
      return;
    }
    const refs = researchSelected.map((c) => c.sourceRef);
    const confirmed = window.confirm(
      `Research the landlord for ${refs.length} selected row${
        refs.length === 1 ? "" : "s"
      }?\n\nThis runs the paid agentic research (up to ~$2 each, so up to ~${(
        refs.length * RESEARCH_COST_CEILING_PER_ROW
      ).toFixed(
        2
      )} total) and can take a few minutes. Rows that already have a landlord are re-researched (and re-billed). The batch researches in waves and continues automatically until every selected row is done.`
    );
    if (!confirmed) {
      return;
    }
    chainFailedResearchCount.current = 0;
    await launchBulkResearch(refs);
  };
  // "Run again" on a row a finished chain never reached: re-launch a one-row
  // chain for it (bounded, mirrors the toolbar launch path). Guarded against an
  // in-flight op so it can't race a running chain.
  const handleRerunResearch = (sourceRef: string) => {
    if (bulkResearchOp !== null) return;
    chainFailedResearchCount.current = 0;
    void launchBulkResearch([sourceRef]);
  };
  // Pull one still-queued row out of the running batch, keeping the rest. Optimistic:
  // mark it cancelled locally so the row + panel button drop the "queued" state at once,
  // then tell the BE (the worker skips it before its turn). On failure, roll the local
  // mark back so the row rejoins the visible queue and surface the error.
  const handleCancelQueuedResearch = async (sourceRef: string) => {
    const op = bulkResearchOp;
    if (!op) return;
    setBulkResearchOp((prev) =>
      prev
        ? { ...prev, cancelledRefs: new Set(prev.cancelledRefs).add(sourceRef) }
        : prev
    );
    try {
      await cancelResearchRow.mutateAsync({
        operationKey: op.operationKey,
        sourceRef,
      });
    } catch {
      setBulkResearchOp((prev) => {
        if (!prev) return prev;
        const next = new Set(prev.cancelledRefs);
        next.delete(sourceRef);
        return { ...prev, cancelledRefs: next };
      });
      setSearchError("Could not cancel that queued row. Try again.");
    }
  };
  const handleConfirmPremium = async () => {
    if (premiumEligible.length === 0) {
      setPremiumModalOpen(false);
      return;
    }
    const refs = premiumEligible.map((c) => c.sourceRef);
    const { candidates: refreshed } =
      await premiumEnrichVoaCandidates.mutateAsync({ sourceRefs: refs });
    const refreshedByRef = new Map(refreshed.map((c) => [c.sourceRef, c]));
    setCandidates((prev) =>
      prev.map((c) => refreshedByRef.get(c.sourceRef) ?? c)
    );
    setPremiumModalOpen(false);
  };

  // Anything the operator has explicitly selected is promotable -
  // including rows in the New / Rejected statuses. Already-Lead rows
  // are filtered because their checkbox is disabled in the table
  // (you can't double-promote). Previously this was gated on
  // `status === "reviewed"`, which meant operators had to make a
  // per-row decision before bulk-promoting; the new flow skips that
  // step so they can promote a search result wholesale and review
  // each lead later via /leads.
  const promotable = visible.filter(
    (c) =>
      selectedRowRefs.has(c.sourceRef) && candidateStatus(c) !== "alreadyLead"
  );
  // For each promotable row, build the "as-displayed" selection
  // payload the BE applies verbatim onto the VOA entry. This is
  // the FE-as-source-of-truth contract: whatever the operator saw
  // when they clicked Promote is what gets persisted - no BE-side
  // defaulting to silently disagree with the rendered cells.
  //
  // Resolution per element matches the cell-render priority:
  //   pending edit > saved selection > top-of-displayed-list default
  //
  // Tenant: top is `tenantMatches[0]`. Landlord: top is the
  // polygon-filtered top via `effectiveTopLandlord`. Building: top
  // is `buildingCandidates[0]`. Owner-occupier defaults to false
  // unless saved.
  const buildPromoteSelection = (c: LeadCandidate) => {
    // Pending state is only meaningful when the operator has
    // actually changed something - the panel emits its initial
    // (defaulted) state on mount with `dirty: false`, which we
    // ignore so `Promote` reflects what the operator saw, not
    // what the panel happened to flush at mount time.
    const pending = pendingSelectionByRef[c.sourceRef];
    const dirtyPending = pending?.dirty ? pending : null;
    const buildingBkey =
      dirtyPending?.buildingBkey ??
      c.selectedBuildingBkey ??
      c.buildingCandidates?.[0]?.bkey ??
      null;
    const landlord =
      dirtyPending?.landlord !== undefined
        ? dirtyPending.landlord
        : c.selectedLandlord && savedLandlordIsValid(c)
          ? c.selectedLandlord
          : (() => {
              const top = effectiveTopLandlord(c);
              // Never promote a below-gate match. The right panel shows it as
              // "none selected", so persisting it here would save a landlord
              // the operator was never shown as chosen.
              if (!top || !isLandlordAutoPickable(top.confidence)) return null;
              return {
                orgId: top.orgId ?? null,
                displayName: top.orgName ?? top.proprietorName,
                source: "hmlr" as const,
                hmlrTitleNumber: top.titleNumber,
              };
            })();
    const tenant =
      dirtyPending?.tenant !== undefined
        ? dirtyPending.tenant
        : c.selectedTenant && savedTenantIsValid(c)
          ? c.selectedTenant
          : (() => {
              const top = c.tenantMatches[0];
              // Same gate as the landlord above and the right panel's
              // default-pick: below it, promote carries no tenant.
              if (!top || !isAutoPickable(top.confidence)) return null;
              const source = top.source ?? "ch";
              return {
                orgId: top.orgId ?? null,
                displayName: top.orgName ?? top.companyName,
                source: source as "ch" | "google_places" | "osm",
                chCompanyNumber:
                  source === "ch" ? (top.companyNumber ?? null) : undefined,
                googlePlaceId:
                  source === "google_places"
                    ? (top.googlePlaceId ?? null)
                    : undefined,
                osmId: source === "osm" ? (top.osmId ?? null) : undefined,
                // Carry the OSM website so a promote/save off the default-pick
                // still hands people-discovery a precise Apollo domain.
                website: source === "osm" ? (top.website ?? null) : undefined,
              };
            })();
    const ownerOccupier =
      dirtyPending?.ownerOccupier ?? c.ownerOccupier ?? false;
    return {
      selectedBuildingBkey: buildingBkey,
      selectedLandlord: landlord,
      selectedTenant: tenant,
      ownerOccupier,
      // Mirrors ownerOccupier above: prefer the panel's unsaved toggle so a bulk
      // Promote off an open, edited row does not silently drop the flag.
      hasPv: dirtyPending?.hasPv ?? c.hasPv ?? false,
    };
  };

  const handlePromote = async () => {
    const tags = promoteTagsRaw
      .split(/[,\n]/)
      .map((t) => t.trim())
      .filter(Boolean);
    // Single-row mode: triggered by the panel's "Promote with tag &
    // next" button. Hands off to the shared executor that the no-tag
    // direct path also uses.
    if (singlePromoteRef && singlePromoteSelections) {
      const ref = singlePromoteRef;
      const payload = singlePromoteSelections;
      setPromoteModalOpen(false);
      setPromoteTagsRaw("");
      setSinglePromoteRef(null);
      setSinglePromoteSelections(null);
      await executeSinglePromote(ref, payload, tags);
      return;
    }

    // Bulk mode: triggered by the toolbar Promote button.
    if (promotable.length === 0) return;
    if (!confirmNoDuplicateBuildingLeads(promotable.map((c) => c.sourceRef))) {
      return;
    }
    const selections: Record<
      string,
      ReturnType<typeof buildPromoteSelection>
    > = {};
    for (const c of promotable) {
      selections[c.sourceRef] = buildPromoteSelection(c);
    }
    const res = await promoteVoaCandidates.mutateAsync({
      sourceRefs: promotable.map((c) => c.sourceRef),
      nearSiteId: nearSiteId || undefined,
      tags: tags.length ? tags : undefined,
      selections,
    });
    setCandidates((prev) =>
      prev.map((c) => {
        const hit = res.created.find((lead) => lead.sourceRef === c.sourceRef);
        return hit ? { ...c, existingLeadId: hit.id } : c;
      })
    );
    if (res.created.length > 0) {
      const tagsBySourceRef: Record<string, string[]> = {};
      for (const lead of res.created) {
        tagsBySourceRef[lead.sourceRef] = lead.tags ?? [];
      }
      setCreatedLeadTagsByRef((prev) => ({
        ...prev,
        ...tagsBySourceRef,
      }));
    }
    setSelectedRowRefs(new Set());
    setPromoteModalOpen(false);
    setPromoteTagsRaw("");
    // Skipped reasons surface so operators don't see a silent partial-
    // skip ("I clicked Promote on 3 rows and only 2 became leads, why?").
    // The BE now auto-marks unreviewed rows so 'not_reviewed' should
    // never appear; remaining reasons are 'already_a_lead' /
    // 'not_found' / 'error: ...'.
    if (res.skipped && res.skipped.length > 0) {
      setSearchError(
        `Promoted ${res.created.length} of ${promotable.length} rows. ` +
          `Skipped ${res.skipped.length}: ` +
          res.skipped
            .map(
              (s) =>
                `${s.sourceRef} (${PROMOTE_SKIP_LABELS[s.reason] ?? s.reason})`
            )
            .join(", ")
      );
    }
  };

  const toggleRow = (ref: string) => {
    setSelectedRowRefs((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });
  };

  // candidate -> initial selections for the edit panel
  const buildingCandidatesFor = (c: LeadCandidate): BuildingCandidate[] =>
    c.buildingCandidates ?? [];

  // Auto-replay the search once on mount when the URL already carries
  // criteria (deep-link / browser back). After that the operator drives
  // the form directly.
  const didAutoSearch = useRef(false);
  useEffect(() => {
    if (didAutoSearch.current) return;
    didAutoSearch.current = true;
    // Auto-search requires a postcodeOutward - the form-submit handler
    // gates on this, so the deep-link / browser-back path mirrors that.
    if ((searchParams.get("postcodeOutward") ?? "").trim()) {
      runSearch({ target: { kind: "postcode" } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compact-layout visibility: at narrow widths show only the
  // currently-primary panel (list when no selection, detail when
  // there is). At wide widths show both side by side.
  const showLeft = !isCompact || !selectedCandidate;
  const showRight = !isCompact || !!selectedCandidate;

  return (
    <Container fluid style={{ padding: 0, flex: 1, minHeight: 0 }}>
      <div style={{ display: "flex", height: "100%" }}>
        {showLeft && (
          <div
            style={{
              width: isCompact ? "100%" : "55%",
              borderRight: isCompact ? "none" : "1px solid #ddd",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "8px 12px",
                borderBottom: "1px solid #eee",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  marginBottom: 6,
                }}
              >
                <h5 style={{ margin: 0 }}>
                  Search for leads
                  <Link
                    to="/leads"
                    style={{
                      marginLeft: 12,
                      fontSize: 12,
                      fontWeight: 400,
                    }}
                  >
                    back to /leads
                  </Link>
                  <Link
                    to="/leads/search/status"
                    style={{
                      marginLeft: 12,
                      fontSize: 12,
                      fontWeight: 400,
                    }}
                  >
                    Search Status
                  </Link>
                </h5>
                <Button
                  size="sm"
                  variant="link"
                  style={{
                    marginLeft: "auto",
                    padding: 0,
                    fontSize: 14,
                  }}
                  onClick={() => setHelpOpen(true)}
                >
                  How does this work?
                </Button>
              </div>
              <Form
                onSubmit={handleSearch}
                style={{ display: "flex", gap: 6, alignItems: "end" }}
              >
                <Form.Group style={{ flex: 2 }}>
                  <Form.Label style={{ fontSize: 11, marginBottom: 1 }}>
                    Near this site
                  </Form.Label>
                  <Select<SiteSearchOption>
                    placeholder={
                      fetchSites.isLoading
                        ? "Loading sites..."
                        : "Search sites..."
                    }
                    value={selectedSite}
                    onChange={(value) => {
                      setNearSite(value);
                      // Re-anchoring near a site makes the next Search a
                      // postcode search again (overriding a prior "Search
                      // this area").
                      activeSearchTargetRef.current = { kind: "postcode" };
                      setSearchTarget({ kind: "postcode" });
                    }}
                    options={siteSelectOptions}
                    // Sites with no resolvable UK postcode are listed but
                    // greyed out - there's nothing to seed a search from.
                    isOptionDisabled={(option) => !option.outcode}
                    isClearable
                    isLoading={fetchSites.isLoading}
                    styles={{
                      control: (baseStyles) => ({
                        ...baseStyles,
                        minHeight: 31,
                        border:
                          "var(--bs-border-width) solid var(--bs-border-color)",
                        borderRadius: "var(--bs-border-radius)",
                        color: "var(--bs-body-color)",
                        fontSize: 14,
                      }),
                      valueContainer: (baseStyles) => ({
                        ...baseStyles,
                        padding: "0 6px",
                      }),
                      input: (baseStyles) => ({
                        ...baseStyles,
                        margin: 0,
                        padding: 0,
                      }),
                      dropdownIndicator: (base) => ({ ...base, padding: 4 }),
                      clearIndicator: (base) => ({ ...base, padding: 4 }),
                      menu: (base) => ({ ...base, zIndex: 5 }),
                    }}
                  />
                  {fetchSites.isError && (
                    <div
                      style={{ fontSize: 11, color: "#dc3545", marginTop: 2 }}
                    >
                      Couldn&rsquo;t load sites - reload the page to try again.
                    </div>
                  )}
                </Form.Group>
                <Form.Group style={{ flex: 1 }}>
                  <Form.Label
                    style={{
                      fontSize: 11,
                      marginBottom: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    Radius (km)
                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        <Tooltip
                          // Bootstrap's default Tooltip max-width is
                          // 200px which clips the explanation onto 8+
                          // lines. Widening it to 360px keeps the text
                          // to a couple of sentences.
                          style={{ maxWidth: 360 }}
                        >
                          Used as a coarse expansion: every postcode outcode
                          whose centroid sits within this many kilometres of the
                          seed outcode is searched in full. So a small radius
                          can still pull in adjacent outcodes&rsquo; entire
                          areas, and the actual searched footprint is wider than
                          the circle suggests.
                        </Tooltip>
                      }
                    >
                      <span
                        style={{
                          cursor: "help",
                          color: "#888",
                          fontSize: 11,
                        }}
                        aria-label="What does Radius mean?"
                      >
                        ⓘ
                      </span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    // text + local mirror state (radiusKmInput) keeps the caret
                    // put on this page's frequent re-renders; see the
                    // mirror-state comment where it's declared. Allow one
                    // decimal point (radius can be e.g. 2.5).
                    type="text"
                    inputMode="decimal"
                    value={radiusKmInput}
                    onChange={(e) => {
                      // Digits + at most one decimal point (keep the first dot,
                      // drop any later ones) so we never send Number("2.5.3")
                      // = NaN to the backend.
                      const raw = e.target.value.replace(/[^\d.]/g, "");
                      const dot = raw.indexOf(".");
                      const v =
                        dot === -1
                          ? raw
                          : raw.slice(0, dot + 1) +
                            raw.slice(dot + 1).replace(/\./g, "");
                      setRadiusKmInput(v);
                      setRadiusKm(v);
                    }}
                  />
                </Form.Group>
                <Form.Group style={{ flex: 1 }}>
                  <Form.Label style={{ fontSize: 11, marginBottom: 1 }}>
                    Min area (m²)
                  </Form.Label>
                  <Form.Control
                    size="sm"
                    // type="text" + local mirror state (minFloorAreaInput) so
                    // the caret survives this page's frequent re-renders; see
                    // the mirror-state comment where it's declared.
                    type="text"
                    inputMode="numeric"
                    value={minFloorAreaInput}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^\d]/g, "");
                      setMinFloorAreaInput(v);
                      setMinFloorAreaSqm(v);
                    }}
                  />
                </Form.Group>
                <Button
                  size="sm"
                  type="submit"
                  disabled={
                    searchLeadCandidates.isLoading ||
                    pendingOperation !== null ||
                    pendingPreview !== null
                  }
                >
                  {searchLeadCandidates.isLoading ? "..." : "Search"}
                </Button>
              </Form>
            </div>
            {searchError && (
              <Alert variant="danger" style={{ margin: 8 }}>
                {searchError}
              </Alert>
            )}
            {pendingOperation && pendingOperation.kind === "chunked" && (
              <Alert variant="info" style={{ margin: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>
                  Search running in background ·{" "}
                  {chunkedOpRow
                    ? `${chunkedOpRow.chunksCompleted} of ${chunkedOpRow.chunksTotal} batches done`
                    : "starting up..."}
                </div>
                <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                  Results appear in batches of 500 rows as each batch completes.
                  The table auto-refreshes every few seconds.{" "}
                  <Link to="/leads/search/status">View Search Status</Link> for
                  full progress and a Cancel button.
                </div>
                {chunkedOpRow && chunkedOpRow.rowsTotal > 0 && (
                  <div
                    style={{
                      height: 4,
                      background: "#dee2e6",
                      borderRadius: 2,
                      marginTop: 6,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${Math.min(
                          100,
                          ((chunkedOpRow.chunksCompleted *
                            chunkedOpRow.chunkSize) /
                            chunkedOpRow.rowsTotal) *
                            100
                        )}%`,
                        background: "#0d6efd",
                        transition: "width 200ms ease",
                      }}
                    />
                  </div>
                )}
              </Alert>
            )}
            {pendingOperation &&
              pendingOperation.kind !== "chunked" &&
              !premiumAutoFireProgress && (
                <Alert variant="info" style={{ margin: 8 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    <span>
                      {pendingOperation.forceRefetch
                        ? "Force-refetching all data..."
                        : `Enriching ${pendingOperation.totalPendingEnrichment} cold-cache row${pendingOperation.totalPendingEnrichment === 1 ? "" : "s"}...`}
                    </span>
                    {/* Escape hatch: if the worker hangs (e.g. it marked
                    step 4 done but the operation row never finalised),
                    let the operator clear the UI without a hard
                    reload. Doesn't cancel the BE-side operation - the
                    worker keeps running; this just lets them retry
                    the search or do something else. */}
                    <Button
                      size="sm"
                      variant="link"
                      onClick={() => setPendingOperation(null)}
                      style={{
                        fontSize: 11,
                        padding: "0 4px",
                        color: "#6c757d",
                      }}
                      title="Stop showing this progress banner. The BE worker keeps running; this only clears the FE state so you can retry or move on."
                    >
                      cancel
                    </Button>
                  </div>
                  <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                    {(() => {
                      const p = asyncOp.data?.progress;
                      if (!p) return "Starting up...";
                      // The worker reports the true stage count in `p.total`
                      // (5 when it runs the server-side premium pass on a
                      // small search, else 4), so the bar reads "Step X of N"
                      // straight from it - premium is now a real worker stage
                      // rather than a step the FE synthesised on top.
                      const displayTotal = p.total;
                      // `current` is the 0-based index of the stage in
                      // progress; clamp to displayTotal for the brief
                      // "Finalising" tick the worker fires after the last
                      // stage.
                      const stageNum = Math.min(p.current + 1, displayTotal);
                      const stage = `Step ${stageNum} of ${displayTotal}: ${p.message}`;
                      // Within-stage counter is optional. When present:
                      //   "Step 2 of 5: Resolving building polygons (123/250 sites)"
                      // When absent (totalInStage = 0 from a no-op stage,
                      // or the worker hasn't started this stage yet):
                      //   "Step 2 of 5: Resolving building polygons"
                      if (
                        p.totalInStage != null &&
                        p.totalInStage > 0 &&
                        p.processed != null
                      ) {
                        return `${stage} (${p.processed}/${p.totalInStage} sites)`;
                      }
                      return stage;
                    })()}
                  </div>
                  <div
                    style={{
                      height: 4,
                      background: "#dee2e6",
                      borderRadius: 2,
                      marginTop: 6,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        // Weighted progress: outer stage + within-stage
                        // fraction. Each stage owns 1/displayTotal of the
                        // bar so it advances smoothly across every stage
                        // (including the server-side premium stage).
                        width: (() => {
                          const p = asyncOp.data?.progress;
                          if (!p) return "5%";
                          const displayTotal = p.total;
                          const stageFrac =
                            p.totalInStage &&
                            p.totalInStage > 0 &&
                            p.processed != null
                              ? p.processed / p.totalInStage
                              : 0;
                          const overall =
                            (p.current + stageFrac) / Math.max(1, displayTotal);
                          return `${Math.min(100, overall * 100)}%`;
                        })(),
                        background: "#0d6efd",
                        transition: "width 200ms ease",
                      }}
                    />
                  </div>
                </Alert>
              )}
            {premiumAutoFireProgress && (
              // Step 5 phase of the same enrichment banner the worker
              // (steps 1-4) was rendering. Same styling + same header
              // phrasing ("Enriching N cold-cache rows...") so it reads
              // as the same banner moving on to the next step rather
              // than a fresh alert popping up. The phase swap happens
              // on a single render via the (set premium, then clear
              // pendingOperation) order in the runSearch success path.
              <Alert variant="info" style={{ margin: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>
                  Enriching {premiumAutoFireProgress.eligibleRefs} cold-cache
                  row{premiumAutoFireProgress.eligibleRefs === 1 ? "" : "s"}
                  ...
                </div>
                <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                  Step 5 of 5: Premium enrichment (Google Places paid pass) -
                  batch{" "}
                  {Math.min(
                    premiumAutoFireProgress.done + 1,
                    premiumAutoFireProgress.total
                  )}{" "}
                  of {premiumAutoFireProgress.total}. The $ adorners land
                  progressively as each batch returns.
                </div>
                <div
                  style={{
                    height: 4,
                    background: "#dee2e6",
                    borderRadius: 2,
                    marginTop: 6,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      // Bar fills the 4/5..5/5 segment so steps 1-4
                      // visually carry over (the worker stage hit 4/5
                      // = 80%, premium completion = 100%).
                      width: `${Math.min(
                        100,
                        80 +
                          (premiumAutoFireProgress.done /
                            Math.max(1, premiumAutoFireProgress.total)) *
                            20
                      )}%`,
                      background: "#0d6efd",
                      transition: "width 200ms ease",
                    }}
                  />
                </div>
              </Alert>
            )}
            {resultMeta && resultMeta.truncated && !pendingOperation && (
              <Alert variant="warning" style={{ margin: 8, fontSize: 12 }}>
                Search for{" "}
                <em>
                  {summariseSearchTerms({
                    postcodeOutward,
                    minFloorAreaSqm,
                    radiusKm,
                    nearSiteName: selectedSite?.name,
                  })}
                </em>{" "}
                matches{" "}
                <strong>{resultMeta.totalMatched.toLocaleString()}</strong> rows
                (returning the top {resultMeta.totalReturned.toLocaleString()}).
                Narrow the radius or floor-area filter to see more.
              </Alert>
            )}
            {(() => {
              const degradedRows = visible.filter(
                (c) => c.enrichmentDegradedSources.length > 0
              );
              if (degradedRows.length === 0 || pendingOperation) return null;
              // Aggregate which sources are missing across all degraded
              // rows so the banner copy names them. Set ordering follows
              // first-encounter so the typical "OpenStreetMap" appears
              // first (matches what the per-row badge tooltip shows).
              const sourcesSeen: string[] = [];
              for (const c of degradedRows) {
                for (const s of c.enrichmentDegradedSources) {
                  if (!sourcesSeen.includes(s)) sourcesSeen.push(s);
                }
              }
              return (
                <Alert variant="warning" style={{ margin: 8, fontSize: 12 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong>
                        {degradedRows.length.toLocaleString()} row
                        {degradedRows.length === 1 ? "" : "s"} have missing data
                      </strong>{" "}
                      from {formatDegradedSources(sourcesSeen)}. The upstream
                      was rate-limited or unavailable when these rows were
                      enriched. Click to retry just the missing source
                      {sourcesSeen.length === 1 ? "" : "s"} - the rest of the
                      row stays cached.
                    </div>
                    <Button
                      size="sm"
                      variant="warning"
                      style={{ flexShrink: 0, whiteSpace: "nowrap" }}
                      onClick={handleRefetchDegraded}
                      disabled={refetchDegradedProgress !== null}
                    >
                      {refetchDegradedProgress
                        ? refetchDegradedProgress.total > 0
                          ? `Retrying ${refetchDegradedProgress.done}/${refetchDegradedProgress.total}...`
                          : "Starting retry..."
                        : `Retry missing data on ${degradedRows.length} row${degradedRows.length === 1 ? "" : "s"}`}
                    </Button>
                  </div>
                </Alert>
              );
            })()}
            {resultMeta &&
              !pendingOperation &&
              (resultMeta.failedRowCount ?? 0) > 0 && (
                <Alert variant="danger" style={{ margin: 8, fontSize: 12 }}>
                  <strong>
                    {resultMeta.failedRowCount?.toLocaleString()} row
                    {resultMeta.failedRowCount === 1 ? "" : "s"} failed to load
                  </strong>{" "}
                  and have been omitted from this list. The error has been
                  reported to engineering automatically. If you need these rows
                  reviewed, share the assessment refs below:
                  <div
                    style={{
                      marginTop: 4,
                      padding: 6,
                      background: "#fff",
                      border: "1px solid #f5c2c7",
                      borderRadius: 4,
                      fontFamily: "monospace",
                      fontSize: 11,
                      maxHeight: 120,
                      overflowY: "auto",
                      wordBreak: "break-all",
                    }}
                  >
                    {(resultMeta.failedRowRefs ?? []).join(", ")}
                  </div>
                </Alert>
              )}
            {selectedRowRefs.size > 0 && (
              <div
                style={{
                  padding: "6px 12px",
                  background: "#fff3cd",
                  borderBottom: "1px solid #ffe69c",
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <span style={{ fontSize: 12 }}>
                  {selectedRowRefs.size} selected:
                </span>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => setPromoteModalOpen(true)}
                  disabled={promotable.length === 0}
                  title={
                    promotable.length < selectedRowRefs.size
                      ? `${selectedRowRefs.size - promotable.length} of the selected row${selectedRowRefs.size - promotable.length === 1 ? " is" : "s are"} already a Lead and will be skipped.`
                      : undefined
                  }
                >
                  Promote {promotable.length} to leads
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => {
                    // Short batches skip the modal: a sub-10s wait
                    // isn't worth the extra click. Long batches still
                    // get a confirm so the operator can cancel.
                    const eta = estimatePremiumEnrichSeconds(
                      premiumEligible.length
                    );
                    if (eta <= AUTO_CONFIRM_ETA_SECONDS) {
                      handleConfirmPremium();
                    } else {
                      setPremiumModalOpen(true);
                    }
                  }}
                  disabled={
                    premiumEligible.length === 0 ||
                    premiumEnrichVoaCandidates.isLoading
                  }
                  title="Run paid Google Places APIs against the selected rows. Skips rows that already have premium data."
                >
                  {premiumEnrichVoaCandidates.isLoading
                    ? "Enriching..."
                    : `Enrich ${premiumEligible.length} (${formatEnrichCost(
                        premiumEligible.length
                      )})`}
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={handleBulkResearch}
                  disabled={
                    researchSelected.length === 0 ||
                    bulkResearchOp !== null ||
                    bulkResearchVoaLandlords.isLoading
                  }
                  title="Run the paid agentic landlord research (up to ~$2/row) on every selected row. Already-researched rows are re-researched and re-billed. A limited number run per batch - re-run for the rest."
                >
                  {bulkResearchOp
                    ? (() => {
                        const p = bulkResearchOpPoll.data?.operation?.progress;
                        return p
                          ? `Researching landlords ${p.current}/${p.total}...`
                          : "Researching landlords...";
                      })()
                    : `Research landlord (${researchSelected.length})`}
                </Button>
                <Button
                  size="sm"
                  variant="outline-warning"
                  onClick={handleBulkRefetch}
                  disabled={bulkRefetchProgress !== null}
                  title="Bypass the per-row enrichment cache - re-fetch geocodes, building polygons, HMLR deeds, Companies House and OSM from upstream sources."
                >
                  {bulkRefetchProgress
                    ? `Force-refreshing ${bulkRefetchProgress.done}/${bulkRefetchProgress.total}...`
                    : `Force refresh data for ${selectedRowRefs.size} result${selectedRowRefs.size === 1 ? "" : "s"}`}
                </Button>
                <Button
                  size="sm"
                  variant="link"
                  onClick={() => setSelectedRowRefs(new Set())}
                >
                  Clear
                </Button>
              </div>
            )}
            {(() => {
              const statuses: { value: CandStatus; label: string }[] = [
                { value: "new", label: "New" },
                { value: "reviewed", label: "Reviewed" },
                { value: "rejected", label: "Rejected" },
                { value: "alreadyLead", label: "Lead" },
              ];
              const counts = candidates.reduce<Record<CandStatus, number>>(
                (acc, c) => {
                  const s = candidateStatus(c);
                  acc[s] = (acc[s] ?? 0) + 1;
                  return acc;
                },
                { new: 0, reviewed: 0, rejected: 0, alreadyLead: 0 }
              );
              const total = candidates.length;
              return (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 11,
                    padding: "4px 8px 6px",
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ color: "#666", marginRight: 4 }}>Filter:</span>
                  <Button
                    size="sm"
                    variant={
                      statusFilter == null ? "primary" : "outline-secondary"
                    }
                    style={{ fontSize: 11, padding: "2px 8px" }}
                    onClick={() => setStatusFilter(null)}
                  >
                    All ({total})
                  </Button>
                  {statuses.map((s) => {
                    const active = statusFilter === s.value;
                    return (
                      <Button
                        key={s.value}
                        size="sm"
                        variant={active ? "primary" : "outline-secondary"}
                        style={{ fontSize: 11, padding: "2px 8px" }}
                        onClick={() => selectStatusFilter(s.value)}
                        disabled={counts[s.value] === 0}
                      >
                        {s.label} ({counts[s.value]})
                      </Button>
                    );
                  })}
                </div>
              );
            })()}
            <div style={{ flex: 1, overflowY: "auto" }}>
              {/*
              Set the default cell font-size on the table so unstyled
              cells (Tenant, Landlord, Status) inherit a consistent
              12px instead of bootstrap's default ~14px. Cells that
              need a different size (address main 12, postcode 11,
              confidence badge 10) can still override locally.
            */}
              <Table size="sm" hover style={{ marginBottom: 0, fontSize: 12 }}>
                <thead
                  style={{ position: "sticky", top: 0, background: "#fff" }}
                >
                  <tr>
                    <th style={{ width: 28 }}>
                      {(() => {
                        // Select-all picks up every visible row. Other
                        // bulk actions (Force refresh, Premium enrich)
                        // are legitimate on already-Lead rows so we
                        // shouldn't preemptively exclude them at the
                        // select step. Promote already filters out
                        // already-Lead rows itself (via `promotable`)
                        // so selecting them is safe - they just get
                        // skipped from that one action.
                        const selectableRefs = visible.map((c) => c.sourceRef);
                        const allSelected =
                          selectableRefs.length > 0 &&
                          selectableRefs.every((ref) =>
                            selectedRowRefs.has(ref)
                          );
                        const someSelected =
                          !allSelected &&
                          selectableRefs.some((ref) =>
                            selectedRowRefs.has(ref)
                          );
                        return (
                          <Form.Check
                            checked={allSelected}
                            ref={(el: HTMLInputElement | null) => {
                              // Indeterminate state ("some but not all
                              // selected") isn't a controlled prop in
                              // bootstrap, set it via the DOM ref.
                              if (el) el.indeterminate = someSelected;
                            }}
                            disabled={selectableRefs.length === 0}
                            onChange={() => {
                              if (allSelected) {
                                // Clear only the rows we just selected
                                // (preserves any prior selection on rows
                                // that aren't currently visible - though
                                // in practice the table shows everything).
                                setSelectedRowRefs((prev) => {
                                  const next = new Set(prev);
                                  for (const ref of selectableRefs) {
                                    next.delete(ref);
                                  }
                                  return next;
                                });
                              } else {
                                setSelectedRowRefs((prev) => {
                                  const next = new Set(prev);
                                  for (const ref of selectableRefs) {
                                    next.add(ref);
                                  }
                                  return next;
                                });
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={
                              allSelected
                                ? "Deselect all rows"
                                : "Select all rows"
                            }
                          />
                        );
                      })()}
                    </th>
                    <SortHeader
                      label="Value"
                      sortKey="leadValue"
                      sort={sort}
                      setSort={setSort}
                    />
                    <SortHeader
                      label="Conf"
                      sortKey="overallConfidence"
                      sort={sort}
                      setSort={setSort}
                    />
                    <SortHeader
                      label="Address"
                      sortKey="address"
                      sort={sort}
                      setSort={setSort}
                    />
                    <SortHeader
                      label="Building (m²)"
                      sortKey="surfaceAreaSqm"
                      sort={sort}
                      setSort={setSort}
                    />
                    <SortHeader
                      label="Tnt"
                      sortKey="tenantConfidence"
                      sort={sort}
                      setSort={setSort}
                    />
                    <SortHeader
                      label="Tenant"
                      sortKey="tenantName"
                      sort={sort}
                      setSort={setSort}
                    />
                    <SortHeader
                      label="Lnd"
                      sortKey="landlordConfidence"
                      sort={sort}
                      setSort={setSort}
                    />
                    <SortHeader
                      label="Landlord"
                      sortKey="landlordName"
                      sort={sort}
                      setSort={setSort}
                    />
                    <SortHeader
                      label="Status"
                      sortKey="status"
                      sort={sort}
                      setSort={setSort}
                    />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((c) => {
                    const status = candidateStatus(c);
                    const isFocused = c.sourceRef === selectedSourceRef;
                    const isSelected = selectedRowRefs.has(c.sourceRef);
                    return (
                      <tr
                        key={c.sourceRef}
                        onClick={() => setSelectedSourceRef(c.sourceRef)}
                        className={isFocused ? "table-primary" : undefined}
                        style={{
                          cursor: "pointer",
                          borderLeft: isFocused
                            ? "3px solid #0d6efd"
                            : "3px solid transparent",
                        }}
                      >
                        <td
                          onClick={(e) => {
                            // Stop the row-onClick from firing (otherwise
                            // selecting a row via its checkbox would also
                            // open the right-panel detail view).
                            e.stopPropagation();
                            if (status === "alreadyLead") return;
                            // If the click already hit the input element,
                            // its native onChange has fired (or will fire)
                            // - skip to avoid a double-toggle. Clicks on
                            // the surrounding cell padding fall through
                            // to toggleRow here, which is what makes the
                            // larger cell-sized click target work.
                            if ((e.target as HTMLElement).tagName === "INPUT") {
                              return;
                            }
                            toggleRow(c.sourceRef);
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          <Form.Check
                            checked={isSelected}
                            onChange={() => toggleRow(c.sourceRef)}
                          />
                        </td>
                        <td>
                          <WithTooltip text={leadValueTooltip(c.leadValue)}>
                            <Badge
                              {...leadValueBadgeProps(c.leadValue)}
                              style={{ fontSize: 10 }}
                            >
                              {leadValueLabel(c.leadValue)}
                            </Badge>
                          </WithTooltip>
                        </td>
                        <td>
                          {(() => {
                            const overall = overallConfidenceForCandidate(c);
                            const locationConf =
                              locationConfidenceForCandidate(c);
                            const buildingConf = c.matchConfidence ?? null;
                            const combinedConf = combinedBuildingConfidence(
                              locationConf,
                              buildingConf
                            );
                            const landlordTop = effectiveTopLandlord(c);
                            const tenantTop = c.tenantMatches[0] ?? null;
                            const fmt = (n: number | null): string =>
                              n == null ? "—" : `${Math.round(n)}%`;
                            const noBuilding =
                              (c.buildingCandidates?.length ?? 0) === 0;
                            const tooltip =
                              overall == null
                                ? "Overall confidence is not reported - we don't have enough signals on this row yet."
                                : [
                                    `Overall: ${fmt(overall)} (weakest of building / landlord / tenant)`,
                                    `• Building: ${fmt(combinedConf)} (loc ${fmt(locationConf)} + polygon ${fmt(buildingConf)})`,
                                    `• Landlord: ${fmt(landlordTop?.confidence ?? null)}`,
                                    `• Tenant: ${fmt(tenantTop?.confidence ?? null)}`,
                                    ...(noBuilding
                                      ? [
                                          `(Capped at ${NO_BUILDING_OVERALL_CAP}% - no building polygon was resolved.)`,
                                        ]
                                      : []),
                                  ].join("\n");
                            return (
                              <ConfidenceBadge
                                confidence={overall}
                                tooltip={tooltip}
                              />
                            );
                          })()}
                        </td>
                        <td>
                          <div style={{ fontSize: 12 }}>
                            <DuplicateBuildingMark
                              firstAddress={
                                duplicateBuildingByRef.get(c.sourceRef)?.address
                              }
                              present={duplicateBuildingByRef.has(c.sourceRef)}
                            />
                            {c.address ? (
                              formatAddressCasing(c.address)
                            ) : (
                              <em>(no address)</em>
                            )}
                          </div>
                        </td>
                        <td>
                          {(() => {
                            // Pending right-panel edit > saved building
                            // > top candidate. Mirrors the right-panel
                            // logic so the Building cell tracks any
                            // mid-edit building change rather than
                            // always showing the top default. Renders
                            // the building's footprint area + the
                            // combined building-match confidence
                            // (min(location, building polygon)); the
                            // bkey itself is opaque and useless to the
                            // operator.
                            const pendingBuild =
                              pendingSelectionByRef[c.sourceRef];
                            const effectiveBkey =
                              pendingBuild?.buildingBkey ??
                              c.selectedBuildingBkey ??
                              c.buildingCandidates?.[0]?.bkey ??
                              null;
                            const effective =
                              c.buildingCandidates?.find(
                                (b) => b.bkey === effectiveBkey
                              ) ?? c.buildingCandidates?.[0];
                            const area =
                              effective?.surfaceAreaSqm ??
                              c.buildingSurfaceAreaSqm;
                            if (area == null) {
                              return <span style={{ color: "#aaa" }}>-</span>;
                            }
                            const isPendingChange =
                              pendingBuild?.dirty &&
                              pendingBuild.buildingBkey != null &&
                              pendingBuild.buildingBkey !==
                                (c.selectedBuildingBkey ??
                                  c.buildingCandidates?.[0]?.bkey);
                            return (
                              <>
                                {Math.round(area).toLocaleString()}
                                {isPendingChange && (
                                  <span
                                    style={{
                                      color: "#888",
                                      fontSize: 10,
                                      marginLeft: 4,
                                    }}
                                  >
                                    unsaved
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </td>
                        {(() => {
                          // Resolve the tenant cell's contents once
                          // and render across two columns: Conf badge
                          // and Name. Operator-readable left to right;
                          // confidence is sortable, name isn't.
                          // Priority: pending right-panel edit > saved
                          // selectedTenant > default-pick from
                          // tenantMatches.
                          const pending = pendingSelectionByRef[c.sourceRef];
                          let name: React.ReactNode = (
                            <span style={{ color: "#aaa" }}>-</span>
                          );
                          let conf: number | null = null;
                          let confTooltip: string | undefined = undefined;
                          let unsaved = false;
                          const isOwnerOccupied =
                            pending && pending.dirty
                              ? pending.ownerOccupier
                              : (c.ownerOccupier ?? false);
                          if (isOwnerOccupied) {
                            // OO: one org is both occupier and owner. Show the
                            // occupier name here; the Landlord column reads
                            // "Owner occupied".
                            const ooOrg =
                              (pending && pending.dirty
                                ? (pending.tenant ?? pending.landlord)
                                : (c.selectedTenant ?? c.selectedLandlord)) ??
                              null;
                            if (ooOrg?.displayName != null) {
                              name = ooOrg.displayName;
                              if (pending && pending.dirty) unsaved = true;
                              // Resolve the occupier's confidence from ITS OWN
                              // source list (OO doesn't clear the matches, so the
                              // badge shouldn't blank). The owner org can sit in
                              // the tenant OR the landlord slot, so resolve by
                              // source like the sibling Landlord column - never
                              // fall back to a different company's match. This
                              // agrees with the right panel, which scores the same
                              // org across HMLR + agentic + tenant.
                              if (ooOrg.source === "agentic") {
                                conf = agenticConfidenceForSelection(
                                  c.agenticMatches,
                                  ooOrg
                                );
                                confTooltip =
                                  conf != null
                                    ? "Agentic research confidence"
                                    : undefined;
                              } else if (ooOrg.source === "hmlr") {
                                conf = landlordConfidenceForSelection(
                                  c.hmlrMatches,
                                  ooOrg
                                );
                                const match = c.hmlrMatches.find(
                                  (m) => m.confidence === conf
                                );
                                confTooltip = buildLandlordTooltip(
                                  match ?? null
                                );
                              } else {
                                conf = tenantConfidenceForSelection(
                                  c.tenantMatches,
                                  ooOrg
                                );
                                const match = c.tenantMatches.find(
                                  (m) => m.confidence === conf
                                );
                                confTooltip = buildTenantTooltip(match ?? null);
                              }
                            }
                          } else if (pending && pending.dirty) {
                            if (pending.tenant) {
                              name = pending.tenant.displayName;
                              unsaved = true;
                              // Resolve the unsaved selection's confidence too,
                              // so the column matches the right panel's number
                              // instead of blanking on a mid-edit pick.
                              conf = tenantConfidenceForSelection(
                                c.tenantMatches,
                                pending.tenant
                              );
                              const match = c.tenantMatches.find(
                                (m) => m.confidence === conf
                              );
                              confTooltip = buildTenantTooltip(match ?? null);
                            }
                          } else if (
                            c.selectedTenant &&
                            savedTenantIsValid(c)
                          ) {
                            conf = tenantConfidenceForSelection(
                              c.tenantMatches,
                              c.selectedTenant
                            );
                            name = c.selectedTenant.displayName;
                            const match = c.tenantMatches.find(
                              (m) => m.confidence === conf
                            );
                            confTooltip = buildTenantTooltip(match ?? null);
                          } else if (c.tenantMatches[0]) {
                            conf = c.tenantMatches[0].confidence;
                            confTooltip = buildTenantTooltip(
                              c.tenantMatches[0]
                            );
                            // Below the auto-pick gate the right panel refuses
                            // to pre-select and reads "N options, none
                            // selected" - so this cell must not render the name
                            // as though it were the row's tenant. The two
                            // surfaces have to agree on what IS selected.
                            name = isAutoPickable(conf) ? (
                              (c.tenantMatches[0].orgName ??
                              c.tenantMatches[0].companyName)
                            ) : (
                              <span style={{ color: "#aaa" }}>
                                {noSelectionOptionsText(
                                  distinctOrgNameCount(
                                    c.tenantMatches.map(
                                      (m) => m.orgName ?? m.companyName
                                    )
                                  )
                                )}
                              </span>
                            );
                          }
                          return (
                            <>
                              <td>
                                <ConfidenceBadge
                                  confidence={conf}
                                  tooltip={confTooltip}
                                />
                              </td>
                              <td>
                                {name}
                                {unsaved && (
                                  <span
                                    style={{
                                      color: "#888",
                                      fontSize: 10,
                                      marginLeft: 4,
                                    }}
                                  >
                                    unsaved
                                  </span>
                                )}
                              </td>
                            </>
                          );
                        })()}
                        {(() => {
                          const pending = pendingSelectionByRef[c.sourceRef];
                          let name: React.ReactNode = (
                            <span style={{ color: "#aaa" }}>-</span>
                          );
                          let conf: number | null = null;
                          let confTooltip: string | undefined = undefined;
                          let unsaved = false;
                          const isOwnerOccupied =
                            pending && pending.dirty
                              ? pending.ownerOccupier
                              : (c.ownerOccupier ?? false);
                          if (isOwnerOccupied) {
                            // OO: the occupier name is in the Tenant column; this
                            // column carries the OO status instead.
                            name = (
                              <span style={{ color: "#6c757d" }}>
                                {OWNER_OCCUPIED_LANDLORD_LABEL}
                              </span>
                            );
                          } else if (pending && pending.dirty) {
                            if (pending.landlord) {
                              name = pending.landlord.displayName;
                              unsaved = true;
                              // Resolve the unsaved selection's confidence too.
                              // Agentic candidates live in agenticMatches, not
                              // hmlrMatches, so fall back to that list.
                              if (pending.landlord.source === "agentic") {
                                conf = agenticConfidenceForSelection(
                                  c.agenticMatches,
                                  pending.landlord
                                );
                                confTooltip =
                                  conf != null
                                    ? "Agentic research confidence"
                                    : undefined;
                              } else {
                                conf = landlordConfidenceForSelection(
                                  c.hmlrMatches,
                                  pending.landlord
                                );
                                const match = c.hmlrMatches.find(
                                  (m) => m.confidence === conf
                                );
                                confTooltip = buildLandlordTooltip(
                                  match ?? null
                                );
                              }
                            }
                          } else if (
                            c.selectedLandlord &&
                            savedLandlordIsValid(c)
                          ) {
                            name = c.selectedLandlord.displayName;
                            if (c.selectedLandlord.source === "agentic") {
                              conf = agenticConfidenceForSelection(
                                c.agenticMatches,
                                c.selectedLandlord
                              );
                              confTooltip =
                                conf != null
                                  ? "Agentic research confidence"
                                  : undefined;
                            } else {
                              conf = landlordConfidenceForSelection(
                                c.hmlrMatches,
                                c.selectedLandlord
                              );
                              const match = c.hmlrMatches.find(
                                (m) => m.confidence === conf
                              );
                              confTooltip = buildLandlordTooltip(match ?? null);
                            }
                          } else {
                            const filtered = filterHmlrToCurrentBuilding(c);
                            const m = filtered[0] ?? null;
                            if (m) {
                              conf = m.confidence;
                              confTooltip = buildLandlordTooltip(m);
                              // Same rule as the Tenant cell: below the
                              // auto-pick gate the right panel pre-selects
                              // nothing, so this cell must read "none
                              // selected" rather than naming a deed that
                              // isn't the row's landlord.
                              name = isLandlordAutoPickable(conf) ? (
                                (m.orgName ?? m.proprietorName)
                              ) : (
                                <span style={{ color: "#aaa" }}>
                                  {noSelectionOptionsText(
                                    distinctOrgNameCount([
                                      ...filtered.map(
                                        (h) => h.orgName ?? h.proprietorName
                                      ),
                                      ...c.agenticMatches.map(
                                        (a) => a.orgName ?? a.proprietorName
                                      ),
                                    ])
                                  )}
                                </span>
                              );
                            } else {
                              // No saved landlord and no HMLR top-pick. If
                              // landlord research has run, mirror the right-pane
                              // OrgSummary status ("N options, none selected" /
                              // "No options available") instead of a bare "-"
                              // that reads as "research never ran". Options the
                              // right panel would show = filtered HMLR + agentic
                              // (filtered is empty in this branch, so it's the
                              // agentic candidates).
                              const status = researchedBlankLandlordStatus(
                                c,
                                filtered.length + c.agenticMatches.length
                              );
                              if (status != null) {
                                name = (
                                  <span style={{ color: "#6c757d" }}>
                                    {status}
                                  </span>
                                );
                              }
                            }
                          }
                          return (
                            <>
                              <td>
                                <ConfidenceBadge
                                  confidence={conf}
                                  tooltip={confTooltip}
                                />
                              </td>
                              <td>
                                {name}
                                {unsaved && (
                                  <span
                                    style={{
                                      color: "#888",
                                      fontSize: 10,
                                      marginLeft: 4,
                                    }}
                                  >
                                    unsaved
                                  </span>
                                )}
                                {(() => {
                                  // Per-row research state: a single right-panel run, the
                                  // in-flight bulk sub-batch, a still-pending chain row, or a
                                  // finished chain's unreached ("not run") row - via the
                                  // shared helper so /leads matches this exactly.
                                  const rs = researchRowChipState({
                                    ref: c.sourceRef,
                                    singleResearchingRef,
                                    bulkRefs: bulkResearchOp?.refs,
                                    bulkCurrent: bulkResearchCurrent,
                                    bulkCancelledRefs:
                                      bulkResearchCancelledRefs,
                                    chainPendingRefs:
                                      bulkResearchChainPendingRefs,
                                    notRunRefs: researchNotRunRefs,
                                    serverOperationKey: c.researchOperationKey,
                                  });
                                  if (rs === "researching") {
                                    return (
                                      <span
                                        style={{
                                          color: "#0d6efd",
                                          fontSize: 10,
                                          marginLeft: 4,
                                        }}
                                      >
                                        researching
                                        {bulkResearchActiveSearches > 0
                                          ? ` (${bulkResearchActiveSearches})`
                                          : ""}
                                        …
                                      </span>
                                    );
                                  }
                                  if (rs === "queued") {
                                    return (
                                      <span
                                        style={{
                                          color: "#888",
                                          fontSize: 10,
                                          marginLeft: 4,
                                        }}
                                      >
                                        queued…
                                      </span>
                                    );
                                  }
                                  if (rs === "not-run") {
                                    return (
                                      <span
                                        style={{
                                          color: "#b8860b",
                                          fontSize: 10,
                                          marginLeft: 4,
                                        }}
                                      >
                                        not run ·{" "}
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleRerunResearch(c.sourceRef);
                                          }}
                                          style={{
                                            padding: 0,
                                            border: "none",
                                            background: "none",
                                            color: "#0d6efd",
                                            fontSize: 10,
                                            cursor: "pointer",
                                          }}
                                        >
                                          run again
                                        </button>
                                      </span>
                                    );
                                  }
                                  return null;
                                })()}
                              </td>
                            </>
                          );
                        })()}
                        <td>
                          <StatusBadge status={status} />
                          {status === "alreadyLead" &&
                            (() => {
                              // Tags lookup: prefer the in-session map
                              // (set when this row was promoted in this
                              // browser session); fall back to the
                              // /leads cross-lookup for rows that were
                              // already leads when the search loaded.
                              const sessionTags =
                                createdLeadTagsByRef[c.sourceRef];
                              if (sessionTags && sessionTags.length > 0) {
                                return sessionTags.map((t) => (
                                  <Badge
                                    key={t}
                                    bg="secondary"
                                    style={{
                                      marginLeft: 4,
                                      fontSize: 10,
                                      fontWeight: 400,
                                    }}
                                  >
                                    {t}
                                  </Badge>
                                ));
                              }
                              const lead = (fetchLeads.data?.leads ?? []).find(
                                (l) => l.id === c.existingLeadId
                              );
                              const tags = lead?.tags ?? [];
                              return tags.map((t) => (
                                <Badge
                                  key={t}
                                  bg="secondary"
                                  style={{
                                    marginLeft: 4,
                                    fontSize: 10,
                                    fontWeight: 400,
                                  }}
                                >
                                  {t}
                                </Badge>
                              ));
                            })()}
                          {c.matchedExistingSiteID && (
                            <Badge
                              bg="warning"
                              text="dark"
                              style={{ marginLeft: 4, fontSize: 10 }}
                            >
                              On platform
                            </Badge>
                          )}
                          <EnrichmentTierBadge
                            premiumEnrichedAt={c.premiumEnrichedAt}
                          />
                          <DegradedBadge
                            sources={c.enrichmentDegradedSources}
                          />
                        </td>
                      </tr>
                    );
                  })}
                  {visible.length === 0 && (
                    <tr>
                      <td colSpan={10} style={{ color: "#999", padding: 20 }}>
                        {searchLeadCandidates.isLoading ||
                        pendingOperation !== null ? (
                          "Loading search results..."
                        ) : resultMeta === null ? (
                          // No search has completed yet (initial / just cleared).
                          "No results yet. Run a search above."
                        ) : candidates.length === 0 ? (
                          // A search ran and matched nothing - point at the
                          // filters that most commonly over-narrow the result.
                          <>
                            No warehouses matched this search. Try lowering the{" "}
                            <strong>Min area (m²)</strong> filter
                            {radiusKm &&
                            activeSearchTargetRef.current.kind === "postcode"
                              ? ", widening the radius,"
                              : ""}{" "}
                            or panning to a different area and searching again.
                          </>
                        ) : (
                          // Rows exist but the status filter hides them all.
                          "No results match the current status filter."
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        )}
        {showRight && (
          <div style={{ width: isCompact ? "100%" : "45%", height: "100%" }}>
            {selectedCandidate ? (
              <LeadEditPanel
                // Search-result triage pane: platform-org mapping is premature
                // (the row isn't a Lead yet), and the "Then" runners-up peek is
                // shown so the operator can compare candidates while triaging.
                mode="search"
                duplicateOfAddress={
                  duplicateBuildingByRef.has(selectedCandidate.sourceRef)
                    ? (duplicateBuildingByRef.get(selectedCandidate.sourceRef)
                        ?.address ?? "another row")
                    : null
                }
                proximityContext={
                  // An already-promoted row carries its own snapshot: show
                  // that, not the live search anchor, or the panel disagrees
                  // with the /leads inbox and with the anchor the outreach
                  // email actually used. The synthesised one is a preview for
                  // rows that have not been promoted yet.
                  selectedCandidate.existingLeadId != null
                    ? ((fetchLeads.data?.leads ?? []).find(
                        (l) => l.id === selectedCandidate.existingLeadId
                      )?.proximityContext ?? null)
                    : searchProximityContext
                }
                // Re-mount the panel when the candidate's premium-enrich
                // status changes. LeadEditPanel has a fair amount of
                // useState-initialised-from-props internal state (default
                // building, picked geocoder, selected location) that
                // doesn't react to prop changes; refetch / premium-enrich
                // can change which geocoder won, which deed candidate
                // is top, etc., so we wipe + re-init by busting the
                // component's identity. SourceRef alone isn't enough -
                // the operator stays on the same row through a refetch
                // - so we include the timestamps that change when the
                // backend rewrote the row's data.
                key={[
                  selectedCandidate.sourceRef,
                  selectedCandidate.geocodedAt ?? "",
                  selectedCandidate.tenantEnrichedAt ?? "",
                  selectedCandidate.premiumEnrichedAt ?? "",
                  selectedCandidate.agenticResearchedAt ?? "",
                ].join("|")}
                address={selectedCandidate.address}
                leadTags={(() => {
                  // If the row was promoted in this session, use the
                  // tags captured at promote time. Otherwise look up
                  // the lead in the /leads cache by existingLeadId.
                  const sessionTags =
                    createdLeadTagsByRef[selectedCandidate.sourceRef];
                  if (sessionTags && sessionTags.length > 0) return sessionTags;
                  const lead = (fetchLeads.data?.leads ?? []).find(
                    (l) => l.id === selectedCandidate.existingLeadId
                  );
                  return lead?.tags ?? [];
                })()}
                buildingCandidates={buildingCandidatesFor(selectedCandidate)}
                hmlrMatches={selectedCandidate.hmlrMatches}
                tenantMatches={selectedCandidate.tenantMatches}
                agenticMatches={selectedCandidate.agenticMatches}
                agenticDecision={selectedCandidate.agenticDecision}
                tenantCommercial={selectedCandidate.tenantCommercial}
                leadValue={selectedCandidate.leadValue}
                initialBuildingBkey={selectedCandidate.selectedBuildingBkey}
                initialLandlord={selectedCandidate.selectedLandlord}
                initialTenant={selectedCandidate.selectedTenant}
                initialOwnerOccupier={selectedCandidate.ownerOccupier}
                initialHasPv={selectedCandidate.hasPv}
                // No linked site exists pre-promotion, so there is no derived
                // half here - the operator's flag is the only input.
                siteExistingPvKwp={null}
                fallbackLat={
                  selectedCandidate.buildingLatitude ??
                  selectedCandidate.latitude
                }
                fallbackLon={
                  selectedCandidate.buildingLongitude ??
                  selectedCandidate.longitude
                }
                inputContext={buildVoaInputContext(selectedCandidate)}
                onSave={(p) => handleSave(p, false)}
                onRecountCommercial={() =>
                  recountCommercial({
                    sourceRefs: [selectedCandidate.sourceRef],
                  })
                }
                recountingCommercial={recountingCommercial}
                // Per-pick saving is a /leads-inbox affordance: here the same
                // write is a VOA review, which stamps reviewedAt/reviewedBy and
                // flips the row New -> Reviewed - never what an org click means.
                onSaveOrgDecision={null}
                // A search result is not a lead yet, so there is no row to PATCH:
                // the flag rides the VOA review write on Save like the other picks.
                onSaveHasPv={null}
                onSaveAndNext={(p) => handleSave(p, true)}
                onPromoteAndNext={handlePromoteAndNext}
                onPromoteWithTagAndNext={handlePromoteWithTagAndNext}
                onSkipToNext={() => {
                  // Triage skip: advance to the next "New" row without
                  // saving anything on the current one. If we're at the
                  // end of the New rows, fall back to closing the panel
                  // (clear selection -> overview map shows again).
                  const next = findNextNew(selectedCandidate.sourceRef);
                  setSelectedSourceRef(next);
                }}
                // Deselect -> the right panel switches back to the overview
                // map with all results (only wired on /leads/search).
                onBackToMap={() => setSelectedSourceRef(null)}
                onReject={handleReject}
                isRejected={selectedCandidate.rejectedAt != null}
                isAlreadyLead={selectedCandidate.existingLeadId != null}
                peopleDiscovery={(() => {
                  // Contacts live on the promoted lead, not the VOA
                  // candidate. When the row is already a lead, wire the
                  // looked-up lead's decisions + contactCandidates so the
                  // Contacts area shows the found people (same as the
                  // inbox). A not-yet-lead row passes null and the panel
                  // renders the "only on a lead" note.
                  if (selectedCandidate.existingLeadId == null) return null;
                  const lead = (fetchLeads.data?.leads ?? []).find(
                    (l) => l.id === selectedCandidate.existingLeadId
                  );
                  if (!lead) return null;
                  return {
                    leadId: lead.id,
                    landlordDecision: lead.landlordDecision,
                    tenantDecision: lead.tenantDecision,
                    contactCandidates: lead.contactCandidates,
                    contactEnrichments: lead.contactEnrichments,
                    hubspotMatches: lead.hubspotMatches,
                  };
                })()}
                onRefetch={handleRefetch}
                refetching={refetchVoaCandidate.isLoading}
                onPremiumEnrich={handlePremiumEnrich}
                premiumEnriching={premiumEnrichVoaCandidates.isLoading}
                onResearchLandlord={handleResearchLandlord}
                // "Researching" is the ACTIVE state only - the single-row run, or the
                // bulk row currently being worked. A bulk row still waiting its turn is
                // `researchQueued` instead, so the button reads "Queued" not "Researching".
                researching={
                  researchVoaLandlord.isLoading ||
                  researchOp?.sourceRef === selectedCandidate.sourceRef ||
                  selectedBulkResearchState === "researching"
                }
                researchQueued={selectedBulkResearchState === "queued"}
                onCancelQueuedResearch={
                  selectedBulkResearchState === "queued"
                    ? () =>
                        handleCancelQueuedResearch(selectedCandidate.sourceRef)
                    : undefined
                }
                researchSearchCount={
                  researchOp?.sourceRef === selectedCandidate.sourceRef
                    ? (researchOpPoll.data?.operation?.progress?.processed ??
                      null)
                    : selectedBulkResearchState === "researching"
                      ? bulkResearchActiveSearches
                      : null
                }
                agenticResearchedAt={selectedCandidate.agenticResearchedAt}
                researchTraceRunId={selectedCandidate.agenticTraceRunId ?? null}
                researchSubjectRef={selectedCandidate.sourceRef}
                onPendingSelectionChange={(pending) => {
                  // Stash the right panel's mid-edit selection on the
                  // currently-focused row so the left-table cells can
                  // render the in-progress state. Cleared when the
                  // operator picks a different row (or the panel
                  // remounts via key change after a refetch).
                  setPendingSelectionByRef((prev) => ({
                    ...prev,
                    [selectedCandidate.sourceRef]: pending,
                  }));
                }}
                enrichmentFetchedAt={{
                  geocodedAt: selectedCandidate.geocodedAt,
                  buildingResolvedAt: selectedCandidate.buildingResolvedAt,
                  hmlrEnrichedAt: selectedCandidate.hmlrEnrichedAt,
                  tenantEnrichedAt: selectedCandidate.tenantEnrichedAt,
                  epcEnrichedAt: selectedCandidate.epcEnrichedAt,
                  premiumEnrichedAt: selectedCandidate.premiumEnrichedAt,
                }}
                enrichmentDegradedSources={
                  selectedCandidate.enrichmentDegradedSources
                }
              />
            ) : (
              <LeadsOverviewMap
                pins={pins}
                anchorSite={anchorSite}
                searchRadiusKm={radiusKm ? Number(radiusKm) : null}
                onPinClick={setSelectedSourceRef}
                fitToken={mapFitToken}
                hideSearchAreaToken={mapSearchToken}
                onSearchArea={handleSearchArea}
                searching={searchLeadCandidates.isLoading}
                initialViewport={overviewViewportRef.current}
                onViewportChange={(v) => {
                  overviewViewportRef.current = v;
                }}
              />
            )}
          </div>
        )}
      </div>

      <Modal
        show={pendingPreview !== null}
        onHide={handleCancelEnrichment}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {pendingPreview?.forceRefetch
              ? "Confirm force-refetch"
              : "Confirm enrichment"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingPreview &&
            (() => {
              const etaSeconds = estimateCheapSearchSeconds(
                pendingPreview.totalPendingEnrichment
              );
              const isSyncPath =
                pendingPreview.totalMatched <= CHUNKED_SEARCH_THRESHOLD;
              return (
                <p>
                  This search will take{" "}
                  <strong>{formatEtaSeconds(etaSeconds)}</strong> and return{" "}
                  <strong>
                    {pendingPreview.totalMatched.toLocaleString()}
                  </strong>{" "}
                  building{pendingPreview.totalMatched === 1 ? "" : "s"}.
                  Confirm, or cancel to narrow the search.
                  {isSyncPath && (
                    <> Once running, this search can&rsquo;t be cancelled.</>
                  )}
                </p>
              );
            })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelEnrichment}>
            Cancel
          </Button>
          <Button
            variant={pendingPreview?.forceRefetch ? "warning" : "primary"}
            onClick={handleConfirmEnrichment}
            disabled={searchLeadCandidates.isLoading}
          >
            {pendingPreview?.forceRefetch ? "Force-refetch all rows" : "Search"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={promoteModalOpen}
        onHide={() => {
          setPromoteModalOpen(false);
          setSinglePromoteRef(null);
          setSinglePromoteSelections(null);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {singlePromoteRef
              ? "Promote candidate to lead"
              : `Promote ${promotable.length} candidate${
                  promotable.length === 1 ? "" : "s"
                } to leads`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Label>Tags (optional)</Form.Label>
          {topTagChips.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                marginBottom: 6,
              }}
            >
              {topTagChips.map((t) => (
                <Button
                  key={t}
                  size="sm"
                  variant="outline-secondary"
                  style={{ fontSize: 11, padding: "2px 8px" }}
                  onClick={() => addTagChipToInput(t)}
                  title="Click to add this tag to the input"
                >
                  + {t}
                </Button>
              ))}
            </div>
          )}
          <Form.Control
            as="textarea"
            rows={2}
            placeholder="e.g. HSBC, Stowmarket neighbors, BS11"
            // Lighter placeholder text - Bootstrap's default is too
            // contrasty for an example string the operator should be
            // able to ignore at a glance.
            style={{ ["--bs-secondary-color" as string]: "#bbb" }}
            value={promoteTagsRaw}
            onChange={(e) => setPromoteTagsRaw(e.target.value)}
          />
          <Form.Text muted>
            Comma- or newline-separated.
            {singlePromoteRef
              ? " Applied to the promoted lead."
              : " Applied to every promoted lead."}
          </Form.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setPromoteModalOpen(false);
              setSinglePromoteRef(null);
              setSinglePromoteSelections(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handlePromote}
            disabled={promoteVoaCandidates.isLoading}
          >
            {promoteVoaCandidates.isLoading
              ? "Promoting..."
              : singlePromoteRef
                ? "Promote & next"
                : "Promote"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={premiumModalOpen}
        onHide={() => setPremiumModalOpen(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Enrich {premiumEligible.length} row
            {premiumEligible.length === 1 ? "" : "s"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Enriching <strong>{premiumEligible.length}</strong> row
            {premiumEligible.length === 1 ? "" : "s"} is estimated to take{" "}
            <strong>
              {formatEtaSeconds(
                estimatePremiumEnrichSeconds(premiumEligible.length)
              )}
            </strong>
            .
          </p>
          {selectedRowRefs.size > premiumEligible.length && (
            <p style={{ color: "#666", fontSize: 13 }}>
              {selectedRowRefs.size - premiumEligible.length} row
              {selectedRowRefs.size - premiumEligible.length === 1
                ? " has"
                : "s have"}{" "}
              already been enriched and will be skipped.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setPremiumModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmPremium}
            disabled={
              premiumEnrichVoaCandidates.isLoading ||
              premiumEligible.length === 0
            }
          >
            {premiumEnrichVoaCandidates.isLoading
              ? "Enriching..."
              : `Enrich ${premiumEligible.length} (${formatEnrichCost(
                  premiumEligible.length
                )})`}
          </Button>
        </Modal.Footer>
      </Modal>

      <LeadsHelpModal
        show={helpOpen}
        variant="search"
        onHide={() => setHelpOpen(false)}
      />
    </Container>
  );
};

export default LeadSearchPage;
